/**
 * Funnel Properties Panel - MA-UI-030 + MA-UI-036
 * 
 * Context-aware right panel for editing selected elements.
 * 
 * Features:
 * - System Message Settings
 * - Prospect Response Settings  
 * - Appearance Settings with Heroicon Picker
 * - Color customization with auto-contrast
 */

import React, { useState, useRef, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import { resolveAppearanceValue, MA305_CALIBRATED_VALUES, addAppearanceOverride, removeAppearanceOverride } from "@/lib/appearanceOverrides";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
  SelectSeparator,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Trash2,
  MessageSquare,
  ArrowRight,
  CheckCircle2,
  XCircle,
  Palette,
  Upload,
  RefreshCw,
  Settings2,
  Loader2,
  Search,
  Grid3X3,
  Calendar as CalendarIcon,  // MA-UI-080: Aliased for panel icon
  ChevronsUpDown,
  // Heroicons for picker
  Home,
  User,
  Heart,
  Star,
  Bell,
  Mail,
  Phone,
  Camera,
  Mic,
  Video,
  Music,
  Image,
  File,
  Folder,
  Calendar,
  Clock,
  MapPin,
  Globe,
  Shield,
  Lock,
  Key,
  CreditCard,
  ShoppingCart,
  Gift,
  Tag,
  Bookmark,
  Flag,
  Award,
  Zap,
  Sun,
  Moon,
  Cloud,
  Umbrella,
  Coffee,
  Car,
  Plane,
  Ship,
  Building,
  Briefcase,
  GraduationCap,
  Book,
  Pencil,
  Scissors,
  Wrench,
  Hammer,
  Target,
  TrendingUp,
  BarChart,
  PieChart,
  Activity,
  Cpu,
  Wifi,
  Bluetooth,
  Battery,
  Power,
  Settings,
  HelpCircle,
  Info,
  AlertCircle,
  AlertTriangle,  // MA-BOOK-002: For booking status warnings (also used for consent warning)
  CheckCircle,
  XCircle as XCircleIcon,
  Plus,
  Minus,
  X,
  Check,
  ChevronRight,
  ChevronDown,
  ArrowUp,
  ArrowDown,
  ExternalLink,
  Link,
  Share,
  Download,
  UploadCloud,
  Trash,
  Edit,
  Copy,
  Clipboard,
  Save,
  Send,
  Reply,
  Forward,
  Archive,
  Inbox,
  Users,
  UserPlus,
  UserCheck,
  ThumbsUp,
  ThumbsDown,
  MessageCircle,
  Eye,
  EyeOff,
  Volume2,
  VolumeX,
  Play,
  Pause,
  SkipForward,
  SkipBack,
  Maximize,
  Minimize,
  Grid,
  List,
  Menu,
  MoreHorizontal,
  MoreVertical,
  Filter,
  Sliders,
  Layers,
  Layout,
  Sidebar,
  Terminal,
  Code,
  Database,
  Server,
  HardDrive,
  Type,  // MA-UI-105: For header settings icon
  FileCheck,  // For consent settings icon
  TextCursorInput, // MA-FE-INPUT-UPDATE: For input field settings icon
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
// MA-FE-079: Import routing components
import { 
  ResponseRoutingEditor, 
  validateResponseStep,
  RouteTargetSelector 
} from "./ResponseStepComponents";
import { api } from "@/lib/api";
import { ConsentEditor } from "@/components/shared/ConsentEditor";

// =============================================================================
// Heroicons Library for Picker
// =============================================================================
import { SUPPORTED_HEADER_ICONS } from "@/lib/headerIcons";

// MA-264: HEROICONS constant removed - now using SUPPORTED_HEADER_ICONS from headerIcons.js
// This ensures icon picker only shows icons that are supported by the renderer

// =============================================================================
// MA-FUNNEL-ROUTING-052: Exit aliases removed. No SPECIAL_TARGETS needed.
// Routing dropdowns now show only real node IDs with "Terminal Steps" grouping.
// =============================================================================

// =============================================================================
// Color Presets
// =============================================================================
const COLOR_PRESETS = {
  prospect: ["#007AFF", "#34C759", "#FF9500", "#FF3B30", "#AF52DE", "#5856D6", "#FF2D55", "#00C7BE"],
  system: ["#E9E9EB", "#F2F2F7", "#D1D1D6", "#FFFFFF", "#F0F9FF", "#F0FDF4", "#FFF7ED", "#FEF2F2"],
  container: ["#FFFFFF", "#F9FAFB", "#F3F4F6", "#1F2937", "#111827", "#0F172A", "#FEF3C7", "#ECFDF5"],
  thread: ["#FFFFFF", "#FAFAFA", "#F5F5F5", "#F9FAFB", "#F0F9FF", "#F0FDF4", "#FFFBEB", "#FDF2F8"],
};

// =============================================================================
// Color Picker Component
// =============================================================================
function ColorPicker({ value, onChange, presets, label }) {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="space-y-2">
      <Label className="text-xs text-slate-300">{label}</Label>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="w-full justify-start gap-3 h-9 bg-white/5 border-white/10 hover:bg-white/10"
          >
            <div 
              className="w-5 h-5 rounded border border-white/20"
              style={{ backgroundColor: value }}
            />
            <span className="text-slate-200 font-mono text-xs">{value}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-56 bg-slate-900/95 backdrop-blur-lg border-white/10" align="start">
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-1.5">
              {presets.map((color) => (
                <button
                  key={color}
                  className={cn(
                    "w-8 h-8 rounded border-2 transition-all hover:scale-110",
                    value === color ? "border-white ring-1 ring-indigo-500" : "border-transparent"
                  )}
                  style={{ backgroundColor: color }}
                  onClick={() => {
                    onChange(color);
                    setIsOpen(false);
                  }}
                />
              ))}
            </div>
            <div className="pt-2 border-t border-white/10">
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  className="w-10 h-8 p-0.5 bg-transparent border-white/10 cursor-pointer"
                />
                <Input
                  type="text"
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  className="flex-1 h-8 bg-white/5 text-slate-200 font-mono text-xs border-white/10"
                />
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

// =============================================================================
// Heroicon Picker Component
// =============================================================================
// MA-UI-080 Section 3: Icon preview sizing to match color preview boxes
function HeroiconPicker({ value, onChange, userColor, allowRemove = true }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Filter icons by search
  const filteredIcons = useMemo(() => {
    if (!searchQuery.trim()) return SUPPORTED_HEADER_ICONS;
    const query = searchQuery.toLowerCase();
    return SUPPORTED_HEADER_ICONS.filter(icon => 
      icon.label.toLowerCase().includes(query) ||
      icon.name.toLowerCase().includes(query)
    );
  }, [searchQuery]);
  
  // Get selected icon component
  const selectedIconData = SUPPORTED_HEADER_ICONS.find(i => i.name === value);
  const SelectedIcon = selectedIconData?.component || null;
  const hasIcon = value && selectedIconData;
  
  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="w-full justify-start gap-3 h-10 bg-white/5 border-white/10 hover:bg-white/10"
        >
          {/* MA-UI-080 Section 3: Sized to match color preview (6x6 = 24px) */}
          {hasIcon ? (
            <div 
              className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: userColor || "#007AFF" }}
            >
              <SelectedIcon className="h-3.5 w-3.5 text-white" />
            </div>
          ) : (
            <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0 bg-slate-700 border border-dashed border-slate-500">
              <span className="text-slate-500 text-xs">—</span>
            </div>
          )}
          <span className="text-slate-200 text-sm flex-1 text-left">
            {hasIcon ? selectedIconData.label : "No Icon"}
          </span>
          <Grid3X3 className="h-4 w-4 text-slate-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-72 p-0 bg-slate-900/95 backdrop-blur-lg border-white/10" 
        align="start"
      >
        {/* Search */}
        <div className="p-3 border-b border-white/10">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search icons..."
              className="pl-9 h-9 bg-white/5 text-slate-100 border-white/10 text-sm"
            />
          </div>
        </div>
        
        {/* Remove Icon Option */}
        {allowRemove && (
          <div className="p-2 border-b border-white/10">
            <button
              onClick={() => {
                onChange(null);
                setIsOpen(false);
              }}
              className={cn(
                "w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm",
                "hover:bg-white/10 transition-colors",
                !value ? "bg-indigo-500/20 text-indigo-300" : "text-slate-400"
              )}
            >
              <XCircle className="h-4 w-4" />
              <span>Remove Icon</span>
              {!value && <Check className="h-4 w-4 ml-auto" />}
            </button>
          </div>
        )}
        
        {/* Icon Grid */}
        <ScrollArea className="h-64">
          <div className="p-3 grid grid-cols-6 gap-1">
            {filteredIcons.map((iconData) => {
              const IconComponent = iconData.component;
              const isSelected = value === iconData.name;
              
              return (
                <button
                  key={iconData.name}
                  onClick={() => {
                    onChange(iconData.name);
                    setIsOpen(false);
                  }}
                  className={cn(
                    "w-10 h-10 rounded-lg flex items-center justify-center",
                    "transition-all duration-150",
                    "hover:bg-white/10",
                    isSelected && "bg-indigo-500/30 ring-1 ring-indigo-400"
                  )}
                  title={iconData.label}
                >
                  <IconComponent className={cn(
                    "h-5 w-5",
                    isSelected ? "text-indigo-300" : "text-slate-400"
                  )} />
                </button>
              );
            })}
          </div>
          
          {filteredIcons.length === 0 && (
            <div className="p-8 text-center text-slate-500">
              <p className="text-sm">No icons found</p>
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}

// =============================================================================
// Bot Message Settings Panel (MA-UI-080: Renamed from System Message)
// =============================================================================
function BotMessageSettings({ 
  node, 
  elementId,
  onUpdate, 
  onDelete,
  appearance,
  onAppearanceChange,
  nodes = [],  // MA-FE-079: Add nodes prop for routing
  // MA-ARCH-020B: Edge-based routing handlers
  onSetDefaultRoute,
  onSetTerminal,
}) {
  const content = node?.content || {};
  const isAcknowledgement = elementId?.includes("_ack");
  
  // MA-FE-079: Routing state
  const responses = content.responses || [];
  const isTerminal = content.is_terminal || false;
  const defaultNextStepId = content.default_next_step_id;
  const nodeType = node?.node_type || "";
  const isResponseNode = nodeType === "response" || nodeType === "question" || nodeType === "message";
  
  // MA-UI-100: Local state for debounced input
  const [localMessage, setLocalMessage] = React.useState("");
  const debounceTimerRef = React.useRef(null);
  
  // MA-BE-077: Track latest content in ref to avoid stale closures
  const contentRef = React.useRef(content);
  React.useEffect(() => {
    contentRef.current = content;
  }, [content]);
  
  // Get appearance settings with defaults
  const settings = {
    botColor: "#E9E9EB",
    botTextColor: "#1F2937",
    ...appearance
  };
  
  // Sync local state when node/element changes
  React.useEffect(() => {
    const msg = isAcknowledgement && content.responses?.length > 0
      ? content.responses[0]?.acknowledgement || ""
      : content.message || "";
    setLocalMessage(msg);
  }, [node?.id, elementId, isAcknowledgement]);
  
  // Cleanup debounce timer
  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);
  
  // MA-BE-077: Use contentRef for latest state
  const updateMessage = (newMessage) => {
    const latestContent = contentRef.current;
    if (isAcknowledgement) {
      const updatedResponses = (latestContent.responses || []).map(r => ({
        ...r,
        acknowledgement: newMessage
      }));
      onUpdate({ content: { ...latestContent, responses: updatedResponses } });
    } else {
      onUpdate({ content: { ...latestContent, message: newMessage } });
    }
  };
  
  // MA-UI-080 Section 6: Debounced message update
  const handleMessageChange = (e) => {
    const value = e.target.value;
    setLocalMessage(value);
    
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      updateMessage(value);
    }, 600);
  };
  
  // Commit on blur
  const handleMessageBlur = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    const latestContent = contentRef.current;
    const currentMsg = isAcknowledgement && latestContent.responses?.length > 0
      ? latestContent.responses[0]?.acknowledgement || ""
      : latestContent.message || "";
    if (localMessage !== currentMsg) {
      updateMessage(localMessage);
    }
  };
  
  // MA-305: Handle appearance color changes with override tracking
  const handleAppearanceChange = (key, value) => {
    const updates = { ...settings, [key]: value };
    
    // Keep legacy fields in sync
    if (key === "userColor") {
      updates.prospectColor = value;
    } else if (key === "botColor") {
      updates.systemColor = value;
    }
    
    // MA-305: Track explicit user overrides
    const currentOverrides = appearance?.appearance_overrides || [];
    const colorFields = [
      'headerBackgroundColor',
      'iconBackgroundColor',
      'userColor',
      'botColor',
      'userTextColor',
      'botTextColor',
      'containerColor',
      'threadColor'
    ];
    
    // If changing a color field, add to overrides
    if (colorFields.includes(key)) {
      updates.appearance_overrides = addAppearanceOverride(key, currentOverrides);
    }
    
    onAppearanceChange?.(updates);
  };
  
  // MA-FE-079: Content handlers (non-routing) - MA-BE-077: Use contentRef
  const handleUpdateContent = (updates) => {
    const latestContent = contentRef.current;
    onUpdate({ content: { ...latestContent, ...updates } });
  };
  
  // MA-ARCH-020B: Edge-based routing handlers
  const handleUpdateDefaultRoute = (targetNodeId) => {
    // Use edge-based mutation if available, fall back to node update
    if (onSetDefaultRoute) {
      onSetDefaultRoute(node?.id, targetNodeId);
    } else {
      // Legacy fallback - should not happen in VisualFunnelBuilder context
      // MA-BE-077: Use contentRef
      console.warn('[MA-ARCH-020B] Legacy fallback: BotMessageSettings.handleUpdateDefaultRoute writing to node field directly');
      const latestContent = contentRef.current;
      onUpdate({ content: { ...latestContent, default_next_step_id: targetNodeId } });
    }
  };
  
  const handleToggleTerminal = (checked) => {
    // Use edge-based mutation if available
    if (onSetTerminal) {
      const latestContent = contentRef.current;
      onSetTerminal(node?.id, checked, latestContent.outcome || "qualified");
    } else {
      // Legacy fallback - should not happen in VisualFunnelBuilder context
      // MA-BE-077: Use contentRef
      console.warn('[MA-ARCH-020B] Legacy fallback: BotMessageSettings.handleToggleTerminal writing to node field directly');
      const latestContent = contentRef.current;
      if (checked) {
        onUpdate({
          content: { 
            ...latestContent, 
            is_terminal: true, 
            outcome: latestContent.outcome || "qualified" 
          },
        });
      } else {
        onUpdate({
          content: { ...latestContent, is_terminal: false },
        });
      }
    }
  };
  
  // Filter to only show other nodes (exclude current)
  const availableNodes = (nodes || []).filter(n => n.id !== node?.id);
  
  return (
    <div className="space-y-4">
      {/* Message Text */}
      <div className="space-y-2">
        <Label className="text-xs text-slate-300">Message Text</Label>
        <Textarea
          value={localMessage}
          onChange={handleMessageChange}
          onBlur={handleMessageBlur}
          className="min-h-[100px] bg-white/5 text-slate-100 border-white/10 text-sm resize-none"
          placeholder={isAcknowledgement ? "Great! Let's continue." : "Enter your question..."}
          data-testid="message-text-input"
        />
      </div>
      
      {/* Emoji Picker for Bot Messages */}
      {!isAcknowledgement && (
        <div className="space-y-2">
          <Label className="text-xs text-slate-300">Add Emoji</Label>
          <div className="flex flex-wrap gap-2">
            {["👋", "😊", "🎉", "✨", "💡", "🚀", "👍", "❤️", "🔥", "⭐"].map((emoji) => (
              <button
                key={emoji}
                type="button"
                onClick={() => {
                  const newMessage = localMessage + emoji;
                  setLocalMessage(newMessage);
                  updateMessage(newMessage);
                }}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-lg"
                title={`Add ${emoji}`}
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Typing Delay */}
      {!isAcknowledgement && (
        <div className="space-y-2">
          <Label className="text-xs text-slate-300">Typing Delay</Label>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min="0"
              max="10"
              step="0.5"
              value={content.typing_delay || 1}
              onChange={(e) => {
                // MA-BE-078: Use contentRef.current for latest content
                const latestContent = contentRef.current;
                onUpdate({ 
                  content: { ...latestContent, typing_delay: parseFloat(e.target.value) || 0 } 
                });
              }}
              className="w-20 h-9 bg-white/5 text-slate-100 border-white/10 text-sm"
            />
            <span className="text-xs text-slate-400">sec</span>
          </div>
        </div>
      )}
      
      <Separator className="bg-white/10" />
      
      {/* Bot Message Colors - MA-UI-107: Moved from Appearance panel */}
      <div className="space-y-3">
        <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wider">Bot Message Style</h4>
        
        <ColorPicker
          label="Bot Message Bubble"
          value={settings.botColor}
          onChange={(color) => handleAppearanceChange("botColor", color)}
          presets={COLOR_PRESETS.system}
        />
        
        <ColorPicker
          label="Bot Message Text"
          value={settings.botTextColor}
          onChange={(color) => handleAppearanceChange("botTextColor", color)}
          presets={["#111827", "#1F2937", "#374151", "#4B5563", "#6B7280", "#FFFFFF"]}
        />
      </div>
      
      {/* MA-FE-079: Step Routing - ALWAYS visible for response nodes */}
      {!isAcknowledgement && isResponseNode && (
        <>
          <Separator className="bg-white/10" />
          <StepRoutingSettings
            node={node}
            nodes={availableNodes}
            onUpdate={onUpdate}
            // MA-ARCH-020B: Edge-based routing
            onSetDefaultRoute={onSetDefaultRoute}
            onSetTerminal={onSetTerminal}
          />
        </>
      )}
      
      <Separator className="bg-white/10" />
      
      {/* Delete Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onDelete?.(node.id)}
        className="w-full text-red-400 hover:text-red-300 hover:bg-red-500/10"
      >
        <Trash2 className="h-4 w-4 mr-2" />
        Delete {isAcknowledgement ? "Acknowledgement" : "Message"}
      </Button>
    </div>
  );
}

// =============================================================================
// Step Routing Settings (MA-FE-079: Always-visible routing configuration)
// =============================================================================
function StepRoutingSettings({ 
  node, 
  nodes = [],
  onUpdate,
  // MA-ARCH-020B: Edge-based routing handlers
  onSetDefaultRoute,
  onSetTerminal,
}) {
  const content = node?.content || {};
  const responses = content.responses || [];
  const isTerminal = content.is_terminal || false;
  const defaultNextStepId = content.default_next_step_id;
  
  // MA-BE-078: Track latest content in ref to avoid stale closures
  const contentRef = React.useRef(content);
  React.useEffect(() => {
    contentRef.current = content;
  }, [content]);
  
  // Filter to only show other nodes (exclude current)
  const availableNodes = nodes.filter(n => n.id !== node?.id);
  
  // MA-FE-079: Content handlers (non-routing) - MA-BE-078: Use contentRef
  const handleUpdateContent = (updates) => {
    const latestContent = contentRef.current;
    onUpdate({ content: { ...latestContent, ...updates } });
  };
  
  // MA-ARCH-020B: Edge-based routing handlers
  const handleUpdateDefaultRoute = (targetNodeId) => {
    if (onSetDefaultRoute) {
      onSetDefaultRoute(node?.id, targetNodeId);
    } else {
      // Legacy fallback - should not happen in VisualFunnelBuilder context
      // MA-BE-078: Use contentRef
      console.warn('[MA-ARCH-020B] Legacy fallback: handleUpdateDefaultRoute writing to node field directly');
      const latestContent = contentRef.current;
      onUpdate({ content: { ...latestContent, default_next_step_id: targetNodeId } });
    }
  };
  
  const handleToggleTerminal = (checked) => {
    if (onSetTerminal) {
      const latestContent = contentRef.current;
      onSetTerminal(node?.id, checked, latestContent.outcome || "qualified");
    } else {
      // Legacy fallback - should not happen in VisualFunnelBuilder context
      // MA-BE-078: Use contentRef
      console.warn('[MA-ARCH-020B] Legacy fallback: handleToggleTerminal writing to node field directly');
      const latestContent = contentRef.current;
      if (checked) {
        onUpdate({
          content: { 
            ...latestContent, 
            is_terminal: true, 
            outcome: latestContent.outcome || "qualified" 
          },
        });
      } else {
        onUpdate({
          content: { ...latestContent, is_terminal: false },
        });
      }
    }
  };
  
  return (
    <div className="space-y-4">
      <ResponseRoutingEditor
        content={content}
        responses={responses}
        defaultNextStepId={defaultNextStepId}
        isTerminal={isTerminal}
        allNodes={availableNodes}
        currentNodeId={node?.id}
        onUpdateContent={handleUpdateContent}
        onUpdateDefaultRoute={handleUpdateDefaultRoute}
        onToggleTerminal={handleToggleTerminal}
      />
    </div>
  );
}

// =============================================================================
// MA-FUNNEL-LEAD-002-V2: Step Behavior Settings
// Shared component for step-level configuration available to Consent, Input, Response nodes
// Contains:
// - Lead Submission Step toggle (controls node.metadata.is_submit_step)
// =============================================================================
function StepBehaviorSettings({ 
  node, 
  nodes = [], 
  onUpdate,
}) {
  const metadata = node?.metadata || {};
  const content = node?.content || {};
  const isSubmitStep = metadata.is_submit_step === true;
  const requiresConsent = content.requires_consent || false;
  
  // Check if another node is already the submit step
  const currentSubmitStep = nodes.find(n => n.id !== node?.id && n.metadata?.is_submit_step);
  
  // MA-CONSENT-TOGGLE-ARCH-APPROVAL-045: Toggle submit step with consent auto-enable
  const handleSubmitStepToggle = (checked) => {
    const updates = { 
      metadata: { 
        ...metadata, 
        is_submit_step: checked 
      } 
    };
    
    // Rule 1: Enable submit → enable consent (auto-enable only)
    if (checked && !requiresConsent) {
      updates.content = {
        ...content,
        requires_consent: true,
        submit_button_text: content.submit_button_text || "I Agree & Submit"
      };
    }
    
    onUpdate(updates);
  };
  
  // MA-CONSENT-TOGGLE-ARCH-APPROVAL-045: Handle consent toggle separately
  const handleConsentToggle = (checked) => {
    const updates = {
      content: { ...content, requires_consent: checked }
    };
    
    // Rule 2: Enable consent → enable submit (auto-enable only)
    if (checked && !isSubmitStep) {
      updates.metadata = {
        ...metadata,
        is_submit_step: true
      };
      if (!content.submit_button_text) {
        updates.content.submit_button_text = "I Agree & Submit";
      }
    }
    
    onUpdate(updates);
  };
  
  // Update content field
  const updateContent = (contentUpdates) => {
    onUpdate({
      content: { ...content, ...contentUpdates }
    });
  };
  
  return (
    <div className="space-y-4">
      <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wider">Step Behavior</h4>
      
      {/* Lead Submission Step Toggle */}
      <div className="flex items-center justify-between">
        <div className="space-y-1 flex-1">
          <Label className="text-xs text-slate-300">Lead Submission Step</Label>
          <p className="text-xs text-slate-500">
            When enabled, this step creates and routes the lead to your CRM. This applies to all user responses on this step.
          </p>
        </div>
        <Switch
          checked={isSubmitStep}
          onCheckedChange={handleSubmitStepToggle}
          disabled={!isSubmitStep && !!currentSubmitStep}
          data-testid="submit-step-toggle"
        />
      </div>
      
      {/* Status messages */}
      {currentSubmitStep && !isSubmitStep && (
        <p className="text-xs text-amber-400/80">
          Another step ({currentSubmitStep.metadata?.label || currentSubmitStep.node_type}) is already the submit step. 
          Disable it first to change.
        </p>
      )}
      {isSubmitStep && (
        <p className="text-xs text-green-400/80 flex items-center gap-1">
          <CheckCircle2 className="h-3 w-3" />
          This is the lead submission step
        </p>
      )}
      
      {/* MA-CONSENT-TOGGLE-ARCH-APPROVAL-045: Require Consent Toggle (independent from Submit Step) */}
      <Separator className="bg-white/10" />
      
      <div className="flex items-center justify-between">
        <div className="space-y-1 flex-1">
          <Label className="text-xs text-slate-300">Require Consent</Label>
          <p className="text-xs text-slate-500">
            When enabled, user must accept consent language before submitting. Submission arrow is replaced with a consent CTA button.
          </p>
        </div>
        <Switch
          checked={requiresConsent}
          onCheckedChange={handleConsentToggle}
          data-testid="requires-consent-toggle"
        />
      </div>
    </div>
  );
}

// =============================================================================
// MA-FUNNEL-LEAD-003: Step Behavior Settings Inline (no header)
// MA-PHASE4-BUILDER-CONSENT-UI-039: Enhanced with consent settings
// Used inside User Settings where the Step Behavior section heading already exists
// =============================================================================
function StepBehaviorSettingsInline({ 
  node, 
  nodes = [], 
  onUpdate,
}) {
  const metadata = node?.metadata || {};
  const content = node?.content || {};
  const isSubmitStep = metadata.is_submit_step === true;
  const requiresConsent = content.requires_consent || false;
  
  // Check if another node is already the submit step
  const currentSubmitStep = nodes.find(n => n.id !== node?.id && n.metadata?.is_submit_step);
  
  // Toggle submit step
  // MA-PHASE4-BUILDER-CONSENT-UI-039: Auto-enable consent when submit is enabled
  // MA-CONSENT-TOGGLE-ARCH-APPROVAL-045: Only auto-enable, NEVER auto-disable
  const handleSubmitStepToggle = (checked) => {
    const updates = { 
      metadata: { 
        ...metadata, 
        is_submit_step: checked 
      } 
    };
    
    // Rule 1: Enable submit → enable consent (auto-enable only)
    if (checked && !requiresConsent) {
      updates.content = {
        ...content,
        requires_consent: true,
        submit_button_text: content.submit_button_text || "I Agree & Submit"
      };
    }
    
    // MA-CONSENT-TOGGLE-ARCH-APPROVAL-045: Do NOT auto-disable consent when submit is disabled
    // User may want to keep consent enabled even if submit is off
    
    onUpdate(updates);
  };
  
  // MA-CONSENT-TOGGLE-ARCH-APPROVAL-045: Handle consent toggle separately
  const handleConsentToggle = (checked) => {
    const updates = {
      content: { ...content, requires_consent: checked }
    };
    
    // Rule 2: Enable consent → enable submit (auto-enable only)
    if (checked && !isSubmitStep) {
      updates.metadata = {
        ...metadata,
        is_submit_step: true
      };
      if (!content.submit_button_text) {
        updates.content.submit_button_text = "I Agree & Submit";
      }
    }
    
    // MA-CONSENT-TOGGLE-ARCH-APPROVAL-045: Do NOT auto-disable submit when consent is disabled
    // User may want to keep submit enabled without consent (legacy behavior)
    
    onUpdate(updates);
  };
  
  // Update content field
  const updateContent = (contentUpdates) => {
    onUpdate({
      content: { ...content, ...contentUpdates }
    });
  };
  
  return (
    <div className="space-y-4">
      {/* Lead Submission Step Toggle */}
      <div className="flex items-center justify-between">
        <div className="space-y-1 flex-1">
          <Label className="text-xs text-slate-300">Lead Submission Step</Label>
          <p className="text-xs text-slate-500">
            When enabled, this step creates and routes the lead to your CRM. This applies to all user responses on this step.
          </p>
        </div>
        <Switch
          checked={isSubmitStep}
          onCheckedChange={handleSubmitStepToggle}
          disabled={!isSubmitStep && !!currentSubmitStep}
          data-testid="submit-step-toggle"
        />
      </div>
      
      {/* Status messages */}
      {currentSubmitStep && !isSubmitStep && (
        <p className="text-xs text-amber-400/80">
          Another step ({currentSubmitStep.metadata?.label || currentSubmitStep.node_type}) is already the submit step. 
          Disable it first to change.
        </p>
      )}
      {isSubmitStep && (
        <p className="text-xs text-green-400/80 flex items-center gap-1">
          <CheckCircle2 className="h-3 w-3" />
          This is the lead submission step
        </p>
      )}
      
      {/* MA-CONSENT-TOGGLE-ARCH-APPROVAL-045: Require Consent Toggle (independent from Submit Step) */}
      <Separator className="bg-white/10" />
      
      <div className="flex items-center justify-between">
        <div className="space-y-1 flex-1">
          <Label className="text-xs text-slate-300">Require Consent</Label>
          <p className="text-xs text-slate-500">
            When enabled, user must accept consent language before submitting. Submission arrow is replaced with a consent CTA button.
          </p>
        </div>
        <Switch
          checked={requiresConsent}
          onCheckedChange={handleConsentToggle}
          data-testid="requires-consent-toggle"
        />
      </div>
    </div>
  );
}

// =============================================================================
// User Settings Panel (MA-UI-080: Renamed from Response Settings)
// =============================================================================
function UserSettings({ 
  node, 
  responseId,
  onUpdate, 
  nodes = [],
  appearance,
  onAppearanceChange,
  // MA-ARCH-020B: Edge-based routing handler
  onSetResponseRoute,
  // MA-BE-069: Atomic response deletion handler
  onDeleteResponse,
}) {
  const content = node?.content || {};
  const responses = content.responses || [];
  const responseIndex = responses.findIndex(r => r.id === responseId || `resp_${responses.indexOf(r)}` === responseId);
  const response = responses[responseIndex];
  
  // MA-UI-080 Section 6: Local state for debounced acknowledgement
  const [localAck, setLocalAck] = React.useState(response?.acknowledgement || "");
  // Local state for debounced label editing (prevents overwrite on async mutation)
  const [localLabel, setLocalLabel] = React.useState(response?.label || "");
  const debounceTimerRef = React.useRef(null);
  const labelDebounceRef = React.useRef(null);
  
  // MA-BE-077: Track latest content/responses/response in refs to avoid stale closures
  const contentRef = React.useRef(content);
  const responsesRef = React.useRef(responses);
  const responseRef = React.useRef(response);
  const responseIndexRef = React.useRef(responseIndex);
  React.useEffect(() => {
    contentRef.current = content;
    responsesRef.current = responses;
    responseRef.current = response;
    responseIndexRef.current = responseIndex;
  }, [content, responses, response, responseIndex]);
  
  // Get appearance settings with defaults
  const settings = {
    userColor: "#007AFF",
    userTextColor: "#FFFFFF",
    ...appearance
  };
  
  // Sync local state when response changes
  React.useEffect(() => {
    setLocalAck(response?.acknowledgement || "");
  }, [responseId, response?.acknowledgement]);
  
  React.useEffect(() => {
    setLocalLabel(response?.label || "");
  }, [responseId, response?.label]);
  
  // Cleanup debounce timer
  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (labelDebounceRef.current) clearTimeout(labelDebounceRef.current);
    };
  }, []);
  
  if (!response) {
    return (
      <div className="text-center text-slate-400 py-8">
        <p className="text-sm">Response not found</p>
      </div>
    );
  }
  
  // MA-BE-077: Use refs for latest state
  const updateResponse = (updates) => {
    const latestResponses = responsesRef.current;
    const latestResponse = responseRef.current;
    const latestContent = contentRef.current;
    const latestIndex = responseIndexRef.current;
    const updatedResponses = [...latestResponses];
    updatedResponses[latestIndex] = { ...latestResponse, ...updates };
    onUpdate({ content: { ...latestContent, responses: updatedResponses } });
  };
  
  // MA-ARCH-020B: Handle response routing via edge mutation
  const handleResponseRouteChange = (targetNodeId) => {
    if (onSetResponseRoute && response.id) {
      // Use edge-based mutation
      onSetResponseRoute(node?.id, response.id, targetNodeId);
    } else {
      // Legacy fallback - should not happen in VisualFunnelBuilder context
      console.warn('[MA-ARCH-020B] Legacy fallback: UserSettings.handleResponseRouteChange writing to response field directly');
      updateResponse({ 
        target: targetNodeId,
        next_step_id: targetNodeId 
      });
    }
  };
  
  // MA-UI-080 Section 6: Debounced acknowledgement update
  const handleAckChange = (e) => {
    const value = e.target.value;
    setLocalAck(value);
    
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      updateResponse({ acknowledgement: value });
    }, 600);
  };
  
  // Commit on blur
  const handleAckBlur = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    const latestResponse = responseRef.current;
    if (localAck !== latestResponse?.acknowledgement) {
      updateResponse({ acknowledgement: localAck });
    }
  };
  
  // MA-BE-069: Use atomic delete to avoid version conflicts
  // Previously this caused race conditions by calling onSetResponseRoute (mutation #1) 
  // then onUpdate (mutation #2) with a stale version
  const deleteResponse = () => {
    if (onDeleteResponse && node?.id && response.id) {
      // Use the atomic delete handler (single mutation for edge removal + content update)
      onDeleteResponse(node.id, response.id);
    } else {
      // Legacy fallback - keep for backward compatibility but log warning
      console.warn('[MA-BE-069] Legacy fallback: deleteResponse using non-atomic path');
      // MA-ARCH-020B: Remove the routing edge first if it exists
      if (onSetResponseRoute && response.id) {
        // Passing null/undefined as target removes the edge
        onSetResponseRoute(node?.id, response.id, null);
      }
      // Then remove the response from the array - MA-BE-077: use refs
      const latestResponses = responsesRef.current;
      const latestContent = contentRef.current;
      const latestIndex = responseIndexRef.current;
      const updatedResponses = latestResponses.filter((_, idx) => idx !== latestIndex);
      onUpdate({ content: { ...latestContent, responses: updatedResponses } });
    }
  };
  
  const availableNodes = nodes.filter(n => n.id !== node.id);
  
  return (
    <div className="space-y-4">
      {/* Button Label */}
      <div className="space-y-2">
        <Label className="text-xs text-slate-300">Button Label</Label>
        <Input
          value={localLabel}
          onChange={(e) => {
            const newLabel = e.target.value;
            setLocalLabel(newLabel);
            if (labelDebounceRef.current) clearTimeout(labelDebounceRef.current);
            labelDebounceRef.current = setTimeout(() => {
              updateResponse({ label: newLabel });
            }, 300);
          }}
          className="h-9 bg-white/5 text-slate-100 border-white/10 text-sm"
          placeholder="Yes"
          data-testid="button-label-input"
        />
      </div>
      
      {/* Acknowledgement - MA-FUNNEL-LEAD-003: Moved under Button Label */}
      <div className="space-y-2">
        <Label className="text-xs text-slate-300">Acknowledgement</Label>
        <Input
          value={localAck}
          onChange={handleAckChange}
          onBlur={handleAckBlur}
          className="h-9 bg-white/5 text-slate-100 border-white/10 text-sm"
          placeholder="Great! Let's continue."
        />
        <p className="text-xs text-slate-500">Shown after user selects response</p>
      </div>
      
      <Separator className="bg-white/10" />
      
      {/* MA-FUNNEL-LEAD-003: Step Behavior Section */}
      <div className="space-y-4">
        <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wider">Step Behavior</h4>
        
        {/* Send to Step - MA-ARCH-020B: Uses edge-based routing */}
        <div className="space-y-2">
          <Label className="text-xs text-slate-300">Send to Step</Label>
          <Select
            value={response.next_step_id || response.target || ""}
            onValueChange={handleResponseRouteChange}
          >
            <SelectTrigger className="h-9 bg-white/5 text-slate-100 border-white/10 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900/95 backdrop-blur-lg border-white/10">
              {/* MA-FUNNEL-ROUTING-052: Terminal Steps from real nodes */}
              {availableNodes.filter(n => n.content?.is_terminal).length > 0 && (
                <SelectGroup>
                  <SelectLabel className="text-slate-400 text-xs">Terminal Steps</SelectLabel>
                  {availableNodes.filter(n => n.content?.is_terminal).map(n => {
                    const isQualified = n.content?.outcome === "qualified";
                    return (
                      <SelectItem key={n.id} value={n.id} className="text-slate-200">
                        <div className="flex items-center gap-2">
                          {isQualified ? <CheckCircle2 className="h-3 w-3 text-green-400" /> : <XCircle className="h-3 w-3 text-red-400" />}
                          {n.metadata?.label || n.node_type}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectGroup>
              )}
              
              {availableNodes.filter(n => !n.content?.is_terminal).length > 0 && (
                <>
                  <SelectSeparator className="bg-white/10" />
                  <SelectGroup>
                    <SelectLabel className="text-slate-400 text-xs">Jump to Step</SelectLabel>
                    {availableNodes.filter(n => !n.content?.is_terminal).map(n => (
                      <SelectItem key={n.id} value={n.id} className="text-slate-200">
                        {n.metadata?.label || n.node_type}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </>
              )}
            </SelectContent>
          </Select>
        </div>
        
        {/* MA-FUNNEL-LEAD-003: Lead Submission Step Toggle - Now in User Settings under Step Behavior */}
        <StepBehaviorSettingsInline
          node={node}
          nodes={nodes}
          onUpdate={onUpdate}
        />
      </div>
      
      <Separator className="bg-white/10" />
      
      {/* User Message Colors - MA-UI-107: Moved from Appearance panel */}
      <div className="space-y-3">
        <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wider">User Message Style</h4>
        
        <ColorPicker
          label="User Message Bubble"
          value={settings.userColor}
          onChange={(color) => {
            const updates = { ...settings, userColor: color, prospectColor: color };
            onAppearanceChange?.(updates);
          }}
          presets={COLOR_PRESETS.prospect}
        />
        
        <ColorPicker
          label="User Message Text"
          value={settings.userTextColor}
          onChange={(color) => {
            const updates = { ...settings, userTextColor: color };
            onAppearanceChange?.(updates);
          }}
          presets={["#FFFFFF", "#F9FAFB", "#E5E7EB", "#111827", "#1F2937", "#374151"]}
        />
        
        {/* Button Style - MA-FUNNEL-LEAD-003: Moved under User Message Text */}
        <div className="space-y-2">
          <Label className="text-xs text-slate-300">Button Style</Label>
          <div className="flex gap-2">
            <Button
              variant={response.style !== "outline" ? "default" : "outline"}
              size="sm"
              onClick={() => updateResponse({ style: "filled" })}
              className={cn(
                "flex-1",
                response.style !== "outline" 
                  ? "bg-indigo-500 hover:bg-indigo-600" 
                  : "border-white/20 text-slate-300"
              )}
            >
              Filled
            </Button>
            <Button
              variant={response.style === "outline" ? "default" : "outline"}
              size="sm"
              onClick={() => updateResponse({ style: "outline" })}
              className={cn(
                "flex-1",
                response.style === "outline" 
                  ? "bg-indigo-500 hover:bg-indigo-600" 
                  : "border-white/20 text-slate-300"
              )}
            >
              Outline
            </Button>
          </div>
        </div>
      </div>
      
      <Separator className="bg-white/10" />
      
      {/* Delete Button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={deleteResponse}
        className="w-full text-red-400 hover:text-red-300 hover:bg-red-500/10"
      >
        <Trash2 className="h-4 w-4 mr-2" />
        Delete Button
      </Button>
    </div>
  );
}

// =============================================================================
// Appearance Settings Panel (MA-UI-080: Enhanced with new controls)
// =============================================================================
// MA-224: Updated to use canonical header fields for title and icon
function AppearanceSettings({ appearance, onAppearanceChange, header = null, onHeaderChange, lpTokens = null }) {
  const [iconTab, setIconTab] = useState(appearance?.customIconUrl ? "upload" : "icon");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);
  
  // Default appearance values with new MA-UI-080 fields
  // MA-FUNNEL-BRAND-DEFAULT-011: Brand-neutral by default
  // MA-234: STRICT CANONICAL READ (header.* is ONLY content source)
  const settings = {
    title: header?.title ?? "",
    selectedIcon: header?.icon_type ?? null,
    customIconUrl: appearance?.customIconUrl || null,
    // MA-UI-080 Section 2.1: Header visibility controls
    showHeaderTitle: appearance?.showHeaderTitle !== false,
    showHeaderIcon: appearance?.showHeaderIcon !== false,
    // MA-305: Use resolveAppearanceValue to display rendered values (not stale persisted values)
    // Color pickers show what's actually rendered on canvas
    headerBackgroundColor: (() => {
      const appearanceOverrides = appearance?.appearance_overrides || [];
      const isLightMode = appearance?.surfaceMode === 'light' || lpTokens?.surfaceMode === 'light';
      return resolveAppearanceValue(
        'headerBackgroundColor',
        appearance,
        appearanceOverrides,
        MA305_CALIBRATED_VALUES,
        { headerBackgroundColor: lpTokens?.cardBg },
        lpTokens?.cardBg,
        isLightMode
      );
    })(),
    iconBackgroundColor: (() => {
      const appearanceOverrides = appearance?.appearance_overrides || [];
      const isLightMode = appearance?.surfaceMode === 'light' || lpTokens?.surfaceMode === 'light';
      return resolveAppearanceValue(
        'iconBackgroundColor',
        appearance,
        appearanceOverrides,
        MA305_CALIBRATED_VALUES,
        { iconBackgroundColor: lpTokens?.accent },
        lpTokens?.accent,
        isLightMode
      );
    })(),
    // Message colors - renamed for clarity
    userColor: (() => {
      const appearanceOverrides = appearance?.appearance_overrides || [];
      const isLightMode = appearance?.surfaceMode === 'light' || lpTokens?.surfaceMode === 'light';
      return resolveAppearanceValue(
        'userColor',
        appearance,
        appearanceOverrides,
        MA305_CALIBRATED_VALUES,
        { userColor: lpTokens?.accent },
        lpTokens?.accent,
        isLightMode
      );
    })(),
    botColor: (() => {
      const appearanceOverrides = appearance?.appearance_overrides || [];
      const isLightMode = appearance?.surfaceMode === 'light' || lpTokens?.surfaceMode === 'light';
      return resolveAppearanceValue(
        'botColor',
        appearance,
        appearanceOverrides,
        MA305_CALIBRATED_VALUES,
        { botColor: lpTokens?.cardBg },
        lpTokens?.cardBg,
        isLightMode
      );
    })(),
    // MA-UI-080 Section 2.3 & 2.4: Text colors
    userTextColor: (() => {
      const appearanceOverrides = appearance?.appearance_overrides || [];
      const isLightMode = appearance?.surfaceMode === 'light' || lpTokens?.surfaceMode === 'light';
      return resolveAppearanceValue(
        'userTextColor',
        appearance,
        appearanceOverrides,
        MA305_CALIBRATED_VALUES,
        { userTextColor: lpTokens?.textOnAccent },
        lpTokens?.textOnAccent,
        isLightMode
      );
    })(),
    botTextColor: (() => {
      const appearanceOverrides = appearance?.appearance_overrides || [];
      const isLightMode = appearance?.surfaceMode === 'light' || lpTokens?.surfaceMode === 'light';
      return resolveAppearanceValue(
        'botTextColor',
        appearance,
        appearanceOverrides,
        MA305_CALIBRATED_VALUES,
        { botTextColor: lpTokens?.textPrimary },
        lpTokens?.textPrimary,
        isLightMode
      );
    })(),
    // Container/Response colors
    containerColor: (() => {
      const appearanceOverrides = appearance?.appearance_overrides || [];
      const isLightMode = appearance?.surfaceMode === 'light' || lpTokens?.surfaceMode === 'light';
      return resolveAppearanceValue(
        'containerColor',
        appearance,
        appearanceOverrides,
        MA305_CALIBRATED_VALUES,
        { containerColor: lpTokens?.cardBg },
        lpTokens?.cardBg,
        isLightMode
      );
    })(),
    responseAreaColor: (() => {
      const appearanceOverrides = appearance?.appearance_overrides || [];
      const isLightMode = appearance?.surfaceMode === 'light' || lpTokens?.surfaceMode === 'light';
      return resolveAppearanceValue(
        'responseAreaColor',
        appearance,
        appearanceOverrides,
        MA305_CALIBRATED_VALUES,
        { responseAreaColor: lpTokens?.cardBg },
        lpTokens?.cardBg,
        isLightMode
      );
    })(),
    threadColor: (() => {
      const appearanceOverrides = appearance?.appearance_overrides || [];
      const isLightMode = appearance?.surfaceMode === 'light' || lpTokens?.surfaceMode === 'light';
      return resolveAppearanceValue(
        'threadColor',
        appearance,
        appearanceOverrides,
        MA305_CALIBRATED_VALUES,
        { threadColor: lpTokens?.bgPage },
        lpTokens?.bgPage,
        isLightMode
      );
    })(),
    // Legacy field mapping for backward compatibility
    prospectColor: appearance?.prospectColor ?? lpTokens?.accent,
    systemColor: appearance?.systemColor ?? lpTokens?.cardBg,
  };
  
  // Sync legacy fields with new fields
  if (!appearance?.userColor && appearance?.prospectColor) {
    settings.userColor = appearance.prospectColor;
  }
  if (!appearance?.botColor && appearance?.systemColor) {
    settings.botColor = appearance.systemColor;
  }
  
  const handleChange = (key, value) => {
    // MA-224: Write to canonical header for title and icon
    if (key === "title") {
      onHeaderChange?.({ ...header, title: value });
    } else if (key === "selectedIcon") {
      onHeaderChange?.({ ...header, icon_type: value });
    } else {
      // MA-305: Handle appearance changes with override tracking
      // CRITICAL: Base updates on ORIGINAL appearance, not resolved settings
      // settings is for display only (uses resolveAppearanceValue for rendering)
      // appearance is the canonical source for persistence
      
      // P4-FIX-ICON-APPEARANCE-002: THE OVERRIDE VECTOR IS CARRIED THROUGH.
      //
      // It used to be destructured away here and re-attached ONLY inside the
      // colour branch below, so every NON-colour appearance change --
      // `customIconUrl` (the icon UPLOAD path), `showHeaderIcon`,
      // `showHeaderTitle` -- emitted an appearance with no
      // `appearance_overrides` key at all.
      //
      // That reads as colours changing by themselves. `resolveAppearanceValue`
      // needs `appearanceOverrides.includes(field)` for Priority 1, so with the
      // vector gone every authored colour fell through to the MA-305 calibrated
      // defaults or the brand token. Measured on upload: authored #F3F4F6 header
      // rendered #f1f5f9, #111827 thread rendered #f8fafc. The authored VALUES
      // were still in the object -- only the vector that makes them visible was
      // dropped, which is why reopening the funnel looked correct.
      //
      // And `handleSave` sends `appearance_overrides: appearance?.appearance_overrides || []`,
      // so saving persisted `[]` -- indistinguishable from Reset to Defaults,
      // with the colours stranded in the document and no way back through the UI.
      //
      // This mirrors `clearCustomIcon` below, which already carries the vector
      // through explicitly. Destructured (not read via `appearance?.`) so the
      // spread still cannot reintroduce a stale copy, and re-attached only when
      // the key was actually present, so an appearance that never had one does
      // not gain a fabricated `[]`.
      const { appearance_overrides: overrides, ...cleanAppearance } = appearance || {};
      const updates = { ...cleanAppearance, [key]: value };
      if (overrides !== undefined) {
        updates.appearance_overrides = overrides;
      }
      
      // Keep legacy fields in sync
      if (key === "userColor") {
        updates.prospectColor = value;
      } else if (key === "botColor") {
        updates.systemColor = value;
      }
      
      // MA-305: Track explicit user overrides for color fields
      const colorFields = [
        'headerBackgroundColor',
        'iconBackgroundColor',
        'userColor',
        'botColor',
        'userTextColor',
        'botTextColor',
        'containerColor',
        'responseAreaColor',
        'threadColor'
      ];
      
      if (colorFields.includes(key)) {
        //: unchanged behaviour -- a colour edit still ADDS its own field. Reads
        //: the destructured value above so there is one source for the vector.
        const currentOverrides = overrides || [];
        updates.appearance_overrides = addAppearanceOverride(key, currentOverrides);
        
        console.log('[MA-305-TRACE] FunnelPropertiesPanel - Color changed:', {
          field: key,
          value: value,
          currentOverrides,
          newOverrides: updates.appearance_overrides,
          fullUpdates: updates,
        });
      }
      
      onAppearanceChange(updates);
    }
  };
  
  // P4-FUNNEL-APPEARANCE-01: the icon actions change ONE field.
  //
  // Both of them used to send `{ ...settings, customIconUrl: null }`. `settings`
  // is the resolved DISPLAY object built above by running every colour through
  // `resolveAppearanceValue`, and it carries no `appearance_overrides` key. So
  // either click replaced canonical appearance with resolved values and dropped
  // the override vector; the next Save then sent `appearance_overrides: []`,
  // which `save_draft` persists, and on reload the MA-305 resolver -- which
  // needs `appearanceOverrides.includes(field)` for Priority 1 -- masked every
  // saved colour. Stored correctly, returned correctly, invisible in the UI.
  //
  // This mirrors `handleChange` above: build from `appearance`, carry the
  // override vector through untouched, and change only the one field. It adds
  // no override (clearing an icon is not a colour authoring act) and removes
  // none, so `appearance_overrides: []` keeps meaning exactly what
  // `resetToDefaults` makes it mean.
  const clearCustomIcon = () => {
    const { appearance_overrides: overrides, ...rest } = appearance || {};
    const updates = { ...rest, customIconUrl: null };
    if (overrides !== undefined) {
      updates.appearance_overrides = overrides;
    }
    return updates;
  };

  // MA-UI-080 Section 5: Icon upload with validation
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadError(null);
    
    // Validate file type
    const validTypes = ["image/png", "image/svg+xml", "image/jpeg", "image/webp"];
    if (!validTypes.includes(file.type)) {
      setUploadError("Invalid format. Please use PNG, SVG, JPEG, or WebP.");
      return;
    }
    
    if (file.size > 500 * 1024) {
      setUploadError("File too large. Maximum size is 500KB.");
      return;
    }
    
    // Validate image dimensions.
    //
    // The global is named EXPLICITLY here, not the bare identifier: this
    // module imports a Lucide icon component also called `Image` (see the
    // lucide-react import above), which lexically shadows the browser
    // constructor. Constructing the bare identifier therefore tried to
    // instantiate a React forwardRef object -- it carries
    // `$$typeof === Symbol(react.forward_ref)` and is not newable -- and threw
    // `TypeError: ... is not a constructor` before the upload could start.
    // Qualifying the global keeps this correct whether or not the icon import
    // stays, and the wording above deliberately avoids spelling the unqualified
    // construction so a plain text search for it stays meaningful.
    const img = new window.Image();
    const objectUrl = URL.createObjectURL(file);
    
    img.onload = async () => {
      URL.revokeObjectURL(objectUrl);
      
      // Check if square (with small tolerance)
      const aspectRatio = img.width / img.height;
      if (aspectRatio < 0.9 || aspectRatio > 1.1) {
        setUploadError("Image must be square. Please upload a 1:1 aspect ratio image.");
        return;
      }
      
      // Check minimum size
      if (img.width < 128 || img.height < 128) {
        setUploadError("Image too small. Minimum size is 128×128 pixels.");
        return;
      }
      
      // All validations passed, upload
      setUploading(true);
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("purpose", "funnel_icon");
        
        const response = await api.post("/uploads/image", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        
        handleChange("customIconUrl", response.data.url);
        handleChange("selectedIcon", null);
        toast({ title: "Icon uploaded successfully" });
      } catch (error) {
        setUploadError("Upload failed. Please try again.");
      } finally {
        setUploading(false);
      }
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      setUploadError("Could not read image. Please try a different file.");
    };
    
    img.src = objectUrl;
  };
  
  // MA-305: Reset to theme defaults (remove ALL overrides)
  const resetToDefaults = () => {
    onAppearanceChange({
      title: "",
      selectedIcon: null,
      customIconUrl: null,
      showHeaderTitle: true,
      showHeaderIcon: true,
      // MA-305: Reset removes all overrides, allows MA-305 defaults to apply
      appearance_overrides: [],
      // Keep only non-color fields populated
      // Color fields will resolve to MA-305 defaults when overrides is empty
    });
  };
  
  return (
    <div className="space-y-4">
      {/* Header Section */}
      <div className="space-y-3">
        <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wider">Header</h4>
        
        {/* Title - only shown if title is visible */}
        {settings.showHeaderTitle && (
          <div className="space-y-2">
            <Label className="text-xs text-slate-300">Header Title</Label>
            <Input
              value={settings.title}
              onChange={(e) => handleChange("title", e.target.value)}
              className="h-9 bg-white/5 text-slate-100 border-white/10 text-sm"
              placeholder="Your Brand Name"
              data-testid="header-title-input"
            />
          </div>
        )}
        
        {/* Icon Selection - only shown if icon is visible */}
        {settings.showHeaderIcon && (
          <div className="space-y-2">
            <Label className="text-xs text-slate-300">Header Icon</Label>
            <Tabs value={iconTab} onValueChange={setIconTab}>
              <TabsList className="w-full bg-white/5 h-8">
                <TabsTrigger value="icon" className="flex-1 text-xs data-[state=active]:bg-indigo-500 data-[state=active]:text-white data-[state=active]:shadow-none">Select Icon</TabsTrigger>
                <TabsTrigger value="upload" className="flex-1 text-xs data-[state=active]:bg-indigo-500 data-[state=active]:text-white data-[state=active]:shadow-none">Upload</TabsTrigger>
              </TabsList>
              
              <TabsContent value="icon" className="mt-2">
                <HeroiconPicker
                  value={settings.selectedIcon}
                  onChange={(iconName) => {
                    // MA-224: Write to canonical header.icon_type
                    onHeaderChange?.({ 
                      ...header, 
                      icon_type: iconName 
                    });
                    // Clear custom icon when selecting from library
                    // P4-FUNNEL-APPEARANCE-01: from canonical appearance, so the
                    // explicit colour overrides survive the selection.
                    onAppearanceChange(clearCustomIcon());
                  }}
                  userColor={settings.userColor}
                />
              </TabsContent>
              
              <TabsContent value="upload" className="mt-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/svg+xml,image/jpeg,image/webp"
                  onChange={handleFileUpload}
                  className="hidden"
                />
                
                {/* MA-UI-080 Section 5: Upload specifications */}
                {!settings.customIconUrl && (
                  <div className="mb-3 p-3 bg-slate-800/50 rounded-lg border border-white/5">
                    <p className="text-[10px] font-medium text-slate-300 mb-1.5">Required Icon Specifications:</p>
                    <ul className="text-[10px] text-slate-400 space-y-0.5 list-disc list-inside">
                      <li>Square image (1:1 ratio)</li>
                      <li>Recommended: 256 × 256 pixels</li>
                      <li>Minimum: 128 × 128 pixels</li>
                      <li>PNG, SVG, JPEG, or WebP format</li>
                      <li>Transparent background recommended</li>
                    </ul>
                  </div>
                )}
                
                {/* Upload error message */}
                {uploadError && (
                  <div className="mb-3 p-2 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-400">
                    {uploadError}
                  </div>
                )}
                
                {settings.customIconUrl ? (
                  <div className="flex items-center gap-3 p-3 bg-white/5 rounded-lg">
                    <img 
                      src={settings.customIconUrl} 
                      alt="Custom icon" 
                      className="w-8 h-8 rounded object-contain"
                      style={{ backgroundColor: settings.iconBackgroundColor }}
                    />
                    <div className="flex-1">
                      <p className="text-xs text-slate-300">Custom icon uploaded</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      // P4-FUNNEL-APPEARANCE-01: canonical appearance, not resolved settings
                      onClick={() => onAppearanceChange(clearCustomIcon())}
                      className="h-7 px-2 text-xs text-slate-400 hover:text-red-400"
                    >
                      <X className="h-3 w-3 mr-1" />
                      Remove
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full text-slate-300 border-white/10"
                    disabled={uploading}
                  >
                    {uploading ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="h-3 w-3 mr-2" />
                        Upload Custom Icon
                      </>
                    )}
                  </Button>
                )}
              </TabsContent>
            </Tabs>
          </div>
        )}
        
        {/* MA-UI-107: Header visibility toggles moved to bottom of Header section */}
        <div className="space-y-2 pt-2 border-t border-white/5">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-slate-300">Show Header Title</Label>
            <Switch
              checked={settings.showHeaderTitle}
              onCheckedChange={(checked) => handleChange("showHeaderTitle", checked)}
            />
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-xs text-slate-300">Show Header Icon</Label>
            <Switch
              checked={settings.showHeaderIcon}
              onCheckedChange={(checked) => handleChange("showHeaderIcon", checked)}
            />
          </div>
        </div>
      </div>
      
      <Separator className="bg-white/10" />
      
      {/* Backgrounds - MA-UI-107: Consolidated background colors */}
      <div className="space-y-3">
        <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wider">Backgrounds</h4>
        
        {/* Header Background Color */}
        <ColorPicker
          label="Header Background"
          value={settings.headerBackgroundColor}
          onChange={(color) => handleChange("headerBackgroundColor", color)}
          presets={COLOR_PRESETS.container}
        />
        
        {/* Icon Background Color - only if icon visible */}
        {settings.showHeaderIcon && (
          <ColorPicker
            label="Icon Background"
            value={settings.iconBackgroundColor}
            onChange={(color) => handleChange("iconBackgroundColor", color)}
            presets={COLOR_PRESETS.prospect}
          />
        )}
        
        <ColorPicker
          label="User Section Background"
          value={settings.responseAreaColor}
          onChange={(color) => handleChange("responseAreaColor", color)}
          presets={COLOR_PRESETS.container}
        />
        
        <ColorPicker
          label="Message Thread Background"
          value={settings.threadColor}
          onChange={(color) => handleChange("threadColor", color)}
          presets={COLOR_PRESETS.thread}
        />
      </div>
      
      <Separator className="bg-white/10" />
      
      {/* Reset Button */}
      <Button
        variant="outline"
        size="sm"
        onClick={resetToDefaults}
        className="w-full text-slate-400 border-white/10 hover:text-slate-200"
      >
        <RefreshCw className="h-3 w-3 mr-2" />
        Reset to Defaults
      </Button>
    </div>
  );
}

// =============================================================================
// MA-UI-105: Booking Header Settings Panel
// =============================================================================
function BookingHeaderSettings({ node, onUpdate }) {
  const content = node?.content || {};
  const config = content.config || {};
  const headerSettings = config.header_settings || {};
  
  // Local state for debounced text inputs
  const [localHeading, setLocalHeading] = React.useState(headerSettings.heading || "");
  const [localSubheading, setLocalSubheading] = React.useState(headerSettings.subheading || "");
  const debounceRef = React.useRef(null);
  
  // MA-BE-077: Track latest content/config/headerSettings in refs to avoid stale closures
  const contentRef = React.useRef(content);
  const configRef = React.useRef(config);
  const headerSettingsRef = React.useRef(headerSettings);
  React.useEffect(() => {
    contentRef.current = content;
    configRef.current = config;
    headerSettingsRef.current = headerSettings;
  }, [content, config, headerSettings]);
  
  // Sync local state when node changes
  React.useEffect(() => {
    setLocalHeading(headerSettings.heading || "");
    setLocalSubheading(headerSettings.subheading || "");
  }, [node?.id]);
  
  // MA-BE-077: Use refs for latest state in update functions
  const updateHeaderSetting = (key, value) => {
    const latestHeaderSettings = headerSettingsRef.current;
    const latestConfig = configRef.current;
    const latestContent = contentRef.current;
    const updatedHeaderSettings = { ...latestHeaderSettings, [key]: value };
    const updatedConfig = { ...latestConfig, header_settings: updatedHeaderSettings };
    onUpdate({ content: { ...latestContent, config: updatedConfig } });
  };
  
  const debouncedUpdate = (key, value) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      updateHeaderSetting(key, value);
    }, 500);
  };
  
  return (
    <div className="space-y-4">
      {/* Heading Text */}
      <div className="space-y-2">
        <Label className="text-xs text-slate-300">Heading</Label>
        <Input
          value={localHeading}
          onChange={(e) => {
            setLocalHeading(e.target.value);
            debouncedUpdate("heading", e.target.value);
          }}
          placeholder="Schedule Your 15-Minute Eligibility Review"
          className="bg-white/5 border-white/10 text-slate-100 text-sm"
        />
      </div>
      
      {/* Subheading Text */}
      <div className="space-y-2">
        <Label className="text-xs text-slate-300">Subheading</Label>
        <Input
          value={localSubheading}
          onChange={(e) => {
            setLocalSubheading(e.target.value);
            debouncedUpdate("subheading", e.target.value);
          }}
          placeholder="Book directly with a specialist — not a call center"
          className="bg-white/5 border-white/10 text-slate-100 text-sm"
        />
      </div>
      
      <Separator className="bg-white/10" />
      
      {/* Header Font Color */}
      <div className="space-y-4">
        <ColorPicker
          label="Header Font Color"
          value={headerSettings.header_font_color || "#1F2937"}
          onChange={(color) => updateHeaderSetting("header_font_color", color)}
          presets={["#1F2937", "#374151", "#4B5563", "#111827", "#0F172A"]}
        />
        
        <ColorPicker
          label="Subheader Font Color"
          value={headerSettings.subheader_font_color || "#94A3B8"}
          onChange={(color) => updateHeaderSetting("subheader_font_color", color)}
          presets={["#94A3B8", "#64748B", "#6B7280", "#9CA3AF", "#A1A1AA"]}
        />
      </div>
      
      <Separator className="bg-white/10" />
      
      {/* Header Icons */}
      <div className="space-y-4">
        <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wider">Header Icon</h4>
        <HeroiconPicker
          value={headerSettings.header_icon || null}
          onChange={(icon) => updateHeaderSetting("header_icon", icon)}
        />
        {headerSettings.header_icon && (
          <ColorPicker
            label="Header Icon Color"
            value={headerSettings.header_icon_color || "#63B3ED"}
            onChange={(color) => updateHeaderSetting("header_icon_color", color)}
            presets={["#63B3ED", "#3B82F6", "#6366F1", "#8B5CF6", "#10B981"]}
          />
        )}
        
        <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wider mt-4">Subheader Icon</h4>
        <HeroiconPicker
          value={headerSettings.subheader_icon || null}
          onChange={(icon) => updateHeaderSetting("subheader_icon", icon)}
        />
        {headerSettings.subheader_icon && (
          <ColorPicker
            label="Subheader Icon Color"
            value={headerSettings.subheader_icon_color || "#94A3B8"}
            onChange={(color) => updateHeaderSetting("subheader_icon_color", color)}
            presets={["#94A3B8", "#64748B", "#6B7280", "#9CA3AF", "#A1A1AA"]}
          />
        )}
      </div>
    </div>
  );
}

// =============================================================================
// MA-FUNNEL-ARCH-082: Booking Heading Settings Panel (Individual Focus)
// =============================================================================
function BookingHeadingSettings({ node, onUpdate }) {
  const content = node?.content || {};
  
  // Local state for debounced text input
  const [localHeading, setLocalHeading] = React.useState(content.heading || "");
  const debounceRef = React.useRef(null);
  
  // Track latest content in ref to avoid stale closures
  const contentRef = React.useRef(content);
  React.useEffect(() => {
    contentRef.current = content;
  }, [content]);
  
  // Sync local state when node changes
  React.useEffect(() => {
    setLocalHeading(content.heading || "");
  }, [node?.id, content.heading]);
  
  const debouncedUpdate = (value) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const latestContent = contentRef.current;
      onUpdate({ content: { ...latestContent, heading: value } });
    }, 500);
  };
  
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-xs text-slate-300">Heading Text</Label>
        <Textarea
          value={localHeading}
          onChange={(e) => {
            setLocalHeading(e.target.value);
            debouncedUpdate(e.target.value);
          }}
          placeholder="Schedule Your Consultation"
          className="bg-white/5 border-white/10 text-slate-100 text-sm min-h-[80px]"
        />
        <p className="text-xs text-slate-400">Main heading text for the booking widget</p>
      </div>
    </div>
  );
}

// =============================================================================
// MA-FUNNEL-ARCH-082: Booking Subheading Settings Panel (Individual Focus)
// =============================================================================
function BookingSubheadingSettings({ node, onUpdate }) {
  const content = node?.content || {};
  
  // Local state for debounced text input
  const [localSubheading, setLocalSubheading] = React.useState(content.subheading || "");
  const debounceRef = React.useRef(null);
  
  // Track latest content in ref to avoid stale closures
  const contentRef = React.useRef(content);
  React.useEffect(() => {
    contentRef.current = content;
  }, [content]);
  
  // Sync local state when node changes
  React.useEffect(() => {
    setLocalSubheading(content.subheading || "");
  }, [node?.id, content.subheading]);
  
  const debouncedUpdate = (value) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const latestContent = contentRef.current;
      onUpdate({ content: { ...latestContent, subheading: value } });
    }, 500);
  };
  
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-xs text-slate-300">Subheading Text</Label>
        <Textarea
          value={localSubheading}
          onChange={(e) => {
            setLocalSubheading(e.target.value);
            debouncedUpdate(e.target.value);
          }}
          placeholder="15 min call at your convenience"
          className="bg-white/5 border-white/10 text-slate-100 text-sm min-h-[60px]"
        />
        <p className="text-xs text-slate-400">Subheading text below the main heading</p>
      </div>
    </div>
  );
}


// =============================================================================
// MA-UI-105: Booking Today Settings Panel
// =============================================================================
function BookingTodaySettings({ node, onUpdate }) {
  const content = node?.content || {};
  const config = content.config || {};
  const todaySettings = config.today_settings || {};
  
  // Local state for debounced text input
  const [localHeading, setLocalHeading] = React.useState(todaySettings.heading || "");
  const debounceRef = React.useRef(null);
  
  // MA-BE-077: Track latest content/config/todaySettings in refs
  const contentRef = React.useRef(content);
  const configRef = React.useRef(config);
  const todaySettingsRef = React.useRef(todaySettings);
  React.useEffect(() => {
    contentRef.current = content;
    configRef.current = config;
    todaySettingsRef.current = todaySettings;
  }, [content, config, todaySettings]);
  
  // Sync local state when node changes
  React.useEffect(() => {
    setLocalHeading(todaySettings.heading || "");
  }, [node?.id]);
  
  // MA-BE-077: Use refs for latest state
  const updateTodaySetting = (key, value) => {
    const latestTodaySettings = todaySettingsRef.current;
    const latestConfig = configRef.current;
    const latestContent = contentRef.current;
    const updatedTodaySettings = { ...latestTodaySettings, [key]: value };
    const updatedConfig = { ...latestConfig, today_settings: updatedTodaySettings };
    onUpdate({ content: { ...latestContent, config: updatedConfig } });
  };
  
  const debouncedUpdate = (key, value) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      updateTodaySetting(key, value);
    }, 500);
  };
  
  return (
    <div className="space-y-4">
      {/* Heading */}
      <div className="space-y-2">
        <Label className="text-xs text-slate-300">Heading</Label>
        <Input
          value={localHeading}
          onChange={(e) => {
            setLocalHeading(e.target.value);
            debouncedUpdate("heading", e.target.value);
          }}
          placeholder="Talk Today If You Can"
          className="bg-white/5 border-white/10 text-slate-100 text-sm"
        />
        <p className="text-[10px] text-slate-500">Text displayed on the calendar card</p>
      </div>
      
      <Separator className="bg-white/10" />
      
      {/* Font Color */}
      <ColorPicker
        label="Font Color"
        value={todaySettings.font_color || "#1F2937"}
        onChange={(color) => updateTodaySetting("font_color", color)}
        presets={["#1F2937", "#374151", "#4B5563", "#111827", "#0F172A"]}
      />
      
      <Separator className="bg-white/10" />
      
      {/* Icon */}
      <div className="space-y-4">
        <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wider">Icon</h4>
        <HeroiconPicker
          value={todaySettings.icon || "calendar"}
          onChange={(icon) => updateTodaySetting("icon", icon)}
        />
        
        <ColorPicker
          label="Icon Color"
          value={todaySettings.icon_color || "#63B3ED"}
          onChange={(color) => updateTodaySetting("icon_color", color)}
          presets={["#63B3ED", "#3B82F6", "#6366F1", "#8B5CF6", "#10B981"]}
        />
      </div>
    </div>
  );
}

// =============================================================================
// MA-UI-105: Booking Tomorrow Settings Panel
// =============================================================================
function BookingTomorrowSettings({ node, onUpdate }) {
  const content = node?.content || {};
  const config = content.config || {};
  const tomorrowSettings = config.tomorrow_settings || {};
  
  // Local state for debounced text input
  const [localHeading, setLocalHeading] = React.useState(tomorrowSettings.heading || "");
  const debounceRef = React.useRef(null);
  
  // MA-BE-077: Track latest content/config/tomorrowSettings in refs
  const contentRef = React.useRef(content);
  const configRef = React.useRef(config);
  const tomorrowSettingsRef = React.useRef(tomorrowSettings);
  React.useEffect(() => {
    contentRef.current = content;
    configRef.current = config;
    tomorrowSettingsRef.current = tomorrowSettings;
  }, [content, config, tomorrowSettings]);
  
  // Sync local state when node changes
  React.useEffect(() => {
    setLocalHeading(tomorrowSettings.heading || "");
  }, [node?.id]);
  
  // MA-BE-077: Use refs for latest state
  const updateTomorrowSetting = (key, value) => {
    const latestTomorrowSettings = tomorrowSettingsRef.current;
    const latestConfig = configRef.current;
    const latestContent = contentRef.current;
    const updatedTomorrowSettings = { ...latestTomorrowSettings, [key]: value };
    const updatedConfig = { ...latestConfig, tomorrow_settings: updatedTomorrowSettings };
    onUpdate({ content: { ...latestContent, config: updatedConfig } });
  };
  
  const debouncedUpdate = (key, value) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      updateTomorrowSetting(key, value);
    }, 500);
  };
  
  return (
    <div className="space-y-4">
      {/* Heading */}
      <div className="space-y-2">
        <Label className="text-xs text-slate-300">Heading</Label>
        <Input
          value={localHeading}
          onChange={(e) => {
            setLocalHeading(e.target.value);
            debouncedUpdate("heading", e.target.value);
          }}
          placeholder="Tomorrow"
          className="bg-white/5 border-white/10 text-slate-100 text-sm"
        />
        <p className="text-[10px] text-slate-500">Text displayed on the calendar card</p>
      </div>
      
      <Separator className="bg-white/10" />
      
      {/* Font Color */}
      <ColorPicker
        label="Font Color"
        value={tomorrowSettings.font_color || "#6B7280"}
        onChange={(color) => updateTomorrowSetting("font_color", color)}
        presets={["#6B7280", "#94A3B8", "#64748B", "#9CA3AF", "#A1A1AA"]}
      />
      
      <Separator className="bg-white/10" />
      
      {/* Icon */}
      <div className="space-y-4">
        <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wider">Icon</h4>
        <HeroiconPicker
          value={tomorrowSettings.icon || "calendar"}
          onChange={(icon) => updateTomorrowSetting("icon", icon)}
        />
        
        <ColorPicker
          label="Icon Color"
          value={tomorrowSettings.icon_color || "#94A3B8"}
          onChange={(color) => updateTomorrowSetting("icon_color", color)}
          presets={["#94A3B8", "#64748B", "#6B7280", "#9CA3AF", "#A1A1AA"]}
        />
      </div>
    </div>
  );
}

// =============================================================================
// Consent Step Settings Panel
// =============================================================================

function ConsentStepSettings({ node, onUpdate }) {
  const content = node?.content || {};
  
  // Local state for text fields (immediate update for simplicity)
  const [localTitle, setLocalTitle] = React.useState(content.title || "");
  const [localButtonText, setLocalButtonText] = React.useState(content.button_text || "");
  
  // MA-BE-077: Track latest content in ref to avoid stale closures
  const contentRef = React.useRef(content);
  React.useEffect(() => {
    contentRef.current = content;
  }, [content]);
  
  // Sync local state when node changes
  React.useEffect(() => {
    setLocalTitle(content.title || "");
    setLocalButtonText(content.button_text || "");
  }, [node?.id]);
  
  // Debounce timer ref
  const debounceTimerRef = React.useRef(null);
  
  // MA-BE-077: Use contentRef for latest state - removed useCallback since it was capturing stale content
  const debouncedUpdate = (key, value) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      const latestContent = contentRef.current;
      onUpdate({ content: { ...latestContent, [key]: value } });
    }, 300);
  };
  
  // MA-BE-077: Use contentRef for latest state
  const updateContent = (key, value) => {
    const latestContent = contentRef.current;
    onUpdate({ content: { ...latestContent, [key]: value } });
  };
  
  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b border-white/[0.06] flex-shrink-0">
        <div className="flex items-center gap-2">
          <FileCheck className="h-4 w-4 text-amber-400" />
          <h2 className="text-sm font-medium text-slate-200">Consent Settings</h2>
        </div>
      </div>
      
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-6">
          {/* Warning */}
          <div className="flex items-start gap-3 p-3 rounded-md bg-amber-500/10 border border-amber-500/20">
            <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-amber-500">Important</p>
              <p className="text-amber-500/80">
                Consent language must be manually authored. AI cannot generate compliance or legal text.
              </p>
            </div>
          </div>
          
          {/* Title */}
          <div className="space-y-2">
            <Label className="text-sm text-slate-200/80">Card Title</Label>
            <Input
              value={localTitle}
              onChange={(e) => {
                setLocalTitle(e.target.value);
                debouncedUpdate("title", e.target.value);
              }}
              placeholder="e.g., Consent Required"
              className="bg-white/5 text-slate-100 ring-1 ring-white/10 placeholder:text-slate-400/70"
              data-testid="consent-title-input"
            />
          </div>
          
          {/* Consent Text — MA-PPB-FUNNEL-060: Shared rich text editor */}
          <ConsentEditor
            value={content.consent_text || ""}
            onChange={(html) => {
              debouncedUpdate("consent_text", html);
            }}
            privacyUrl={content.privacy_policy_url || ""}
            termsUrl={content.terms_of_use_url || ""}
            onPrivacyUrlChange={(url) => updateContent("privacy_policy_url", url)}
            onTermsUrlChange={(url) => updateContent("terms_of_use_url", url)}
            placeholder="Enter your TCPA consent language..."
          />
          
          {/* Require Checkbox Toggle */}
          <div className="flex items-center justify-between py-2">
            <div className="space-y-0.5">
              <Label className="text-sm text-slate-200/80">Require Checkbox</Label>
              <p className="text-xs text-slate-200/60">
                User must check a box before clicking the button
              </p>
            </div>
            <Switch
              checked={content.require_checkbox !== false}
              onCheckedChange={(checked) => updateContent("require_checkbox", checked)}
              data-testid="consent-require-checkbox-toggle"
            />
          </div>
          
          {/* Button Text */}
          <div className="space-y-2">
            <Label className="text-sm text-slate-200/80">Button Text</Label>
            <Input
              value={localButtonText}
              onChange={(e) => {
                setLocalButtonText(e.target.value);
                debouncedUpdate("button_text", e.target.value);
              }}
              placeholder="e.g., I Agree"
              className="bg-white/5 text-slate-100 ring-1 ring-white/10 placeholder:text-slate-400/70"
              data-testid="consent-button-text-input"
            />
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}

// =============================================================================
// Consent Title Settings Panel (element-specific)
// =============================================================================
function ConsentTitleSettings({ node, onUpdate }) {
  const content = node?.content || {};
  const [localTitle, setLocalTitle] = React.useState(content.title || "");
  const debounceRef = React.useRef(null);
  
  // MA-BE-078: Track latest content in ref
  const contentRef = React.useRef(content);
  React.useEffect(() => {
    contentRef.current = content;
  }, [content]);
  
  React.useEffect(() => {
    setLocalTitle(content.title || "");
  }, [node?.id]);
  
  // MA-BE-078: Use contentRef for latest content
  const debouncedUpdate = (value) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const latestContent = contentRef.current;
      onUpdate({ content: { ...latestContent, title: value } });
    }, 300);
  };
  
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-xs text-slate-300">Card Title</Label>
        <Input
          value={localTitle}
          onChange={(e) => {
            setLocalTitle(e.target.value);
            debouncedUpdate(e.target.value);
          }}
          placeholder="e.g., Consent Required"
          className="bg-white/5 border-white/10 text-slate-100 text-sm"
          data-testid="consent-title-input"
        />
        <p className="text-xs text-slate-400">
          The title displayed at the top of the consent card
        </p>
      </div>
    </div>
  );
}

// =============================================================================
// Consent Text Settings Panel (element-specific)
// =============================================================================
function ConsentTextSettings({ node, onUpdate }) {
  const content = node?.content || {};
  const debounceRef = React.useRef(null);

  const contentRef = React.useRef(content);
  React.useEffect(() => {
    contentRef.current = content;
  }, [content]);

  const debouncedUpdate = (key, value) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const latestContent = contentRef.current;
      onUpdate({ content: { ...latestContent, [key]: value } });
    }, 300);
  };

  const updateContent = (key, value) => {
    const latestContent = contentRef.current;
    onUpdate({ content: { ...latestContent, [key]: value } });
  };

  return (
    <div className="space-y-4">
      {/* Warning */}
      <div className="flex items-start gap-3 p-3 rounded-md bg-amber-500/10 border border-amber-500/20">
        <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-medium text-amber-500">Important</p>
          <p className="text-amber-500/80 text-xs">
            Consent language must be manually authored. AI cannot generate compliance or legal text.
          </p>
        </div>
      </div>

      {/* MA-PPB-FUNNEL-060: Shared ConsentEditor */}
      <ConsentEditor
        value={content.consent_text || ""}
        onChange={(html) => debouncedUpdate("consent_text", html)}
        privacyUrl={content.privacy_policy_url || ""}
        termsUrl={content.terms_of_use_url || ""}
        onPrivacyUrlChange={(url) => updateContent("privacy_policy_url", url)}
        onTermsUrlChange={(url) => updateContent("terms_of_use_url", url)}
        placeholder="Enter your TCPA consent language..."
      />
    </div>
  );
}

// =============================================================================
// Consent Button Settings Panel (element-specific)
// Includes button text, require checkbox toggle, and Send To dropdown
// MA-CONSENT-TOGGLE-CORRECTION-047: Uses edge-based routing (same as Input Settings)
// =============================================================================
function ConsentButtonSettings({ node, nodes = [], onUpdate, onSetDefaultRoute }) {
  const content = node?.content || {};
  const [localButtonText, setLocalButtonText] = React.useState(content.button_text || "");
  const debounceRef = React.useRef(null);
  
  // MA-BE-078: Track latest content in ref
  const contentRef = React.useRef(content);
  React.useEffect(() => {
    contentRef.current = content;
  }, [content]);
  
  React.useEffect(() => {
    setLocalButtonText(content.button_text || "");
  }, [node?.id]);
  
  // MA-BE-078: Use contentRef for latest content
  const debouncedUpdate = (key, value) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const latestContent = contentRef.current;
      onUpdate({ content: { ...latestContent, [key]: value } });
    }, 300);
  };
  
  // MA-BE-078: Use contentRef for latest content
  const updateContent = (key, value) => {
    const latestContent = contentRef.current;
    onUpdate({ content: { ...latestContent, [key]: value } });
  };
  
  // MA-CONSENT-TOGGLE-CORRECTION-047: Edge-based routing handler (same as Input Settings)
  const handleRoutingChange = (targetNodeId) => {
    if (onSetDefaultRoute) {
      onSetDefaultRoute(node?.id, targetNodeId);
    } else {
      // Legacy fallback
      console.warn('[MA-CONSENT-TOGGLE-CORRECTION-047] Legacy fallback: ConsentButtonSettings writing to node field directly');
      const latestContent = contentRef.current;
      onUpdate({ content: { ...latestContent, default_next_step_id: targetNodeId } });
    }
  };
  
  // Available nodes for "Send To" (excluding current node)
  const availableNodes = nodes.filter(n => n.id !== node?.id);
  
  return (
    <div className="space-y-4">
      {/* Button Text */}
      <div className="space-y-2">
        <Label className="text-xs text-slate-300">Button Text</Label>
        <Input
          value={localButtonText}
          onChange={(e) => {
            setLocalButtonText(e.target.value);
            debouncedUpdate("button_text", e.target.value);
          }}
          placeholder="e.g., I Agree"
          className="bg-white/5 border-white/10 text-slate-100 text-sm"
          data-testid="consent-button-text-input"
        />
      </div>
      
      <Separator className="bg-white/10" />
      
      {/* Require Checkbox Toggle */}
      <div className="flex items-center justify-between py-2">
        <div className="space-y-0.5">
          <Label className="text-xs text-slate-300">Require Checkbox</Label>
          <p className="text-xs text-slate-400">
            User must check a box before clicking the button
          </p>
        </div>
        <Switch
          checked={content.require_checkbox !== false}
          onCheckedChange={(checked) => updateContent("require_checkbox", checked)}
          data-testid="consent-require-checkbox-toggle"
        />
      </div>
      
      <Separator className="bg-white/10" />
      
      {/* MA-CONSENT-TOGGLE-CORRECTION-047: Send To Step - Uses edge-based routing (same edge as Input Settings) */}
      <div className="space-y-2">
        <Label className="text-xs text-slate-300">Send to Step</Label>
        <Select
          value={content.default_next_step_id || ""}
          onValueChange={handleRoutingChange}
        >
          <SelectTrigger className="h-9 bg-white/5 text-slate-100 border-white/10 text-sm" data-testid="consent-button-routing-selector">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-900/95 backdrop-blur-lg border-white/10">
            {/* MA-FUNNEL-ROUTING-052: Terminal Steps from real nodes */}
            {availableNodes.filter(n => n.content?.is_terminal).length > 0 && (
              <SelectGroup>
                <SelectLabel className="text-slate-400 text-xs">Terminal Steps</SelectLabel>
                {availableNodes.filter(n => n.content?.is_terminal).map(n => {
                  const isQualified = n.content?.outcome === "qualified";
                  return (
                    <SelectItem key={n.id} value={n.id} className="text-slate-200">
                      <div className="flex items-center gap-2">
                        {isQualified ? <CheckCircle2 className="h-3 w-3 text-green-400" /> : <XCircle className="h-3 w-3 text-red-400" />}
                        {n.metadata?.label || n.node_type}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectGroup>
            )}
            
            {availableNodes.filter(n => !n.content?.is_terminal).length > 0 && (
              <>
                <SelectSeparator className="bg-white/10" />
                <SelectGroup>
                  <SelectLabel className="text-slate-400 text-xs">Jump to Step</SelectLabel>
                  {availableNodes.filter(n => !n.content?.is_terminal).map(n => (
                    <SelectItem key={n.id} value={n.id} className="text-slate-200">
                      {n.metadata?.label || n.node_type}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </>
            )}
          </SelectContent>
        </Select>
        <p className="text-xs text-slate-400">
          Where to go after the user accepts the consent
        </p>
      </div>
    </div>
  );
}

// =============================================================================
// MA-FE-INPUT-UPDATE: Input Field Settings Panel
// Provides editing controls for Input step:
// - Placeholder/label text
// - Input type (text, email, phone, name)
// - "After submission → Send to Step" routing dropdown
// Uses edge-based routing via onSetDefaultRoute
// =============================================================================
function InputFieldSettings({ 
  node, 
  nodes = [], 
  onUpdate,
  // MA-ARCH-020B: Edge-based routing handler
  onSetDefaultRoute,
}) {
  const content = node?.content || {};
  const metadata = node?.metadata || {};
  const requiresConsent = content.requires_consent || false;
  const [localPlaceholder, setLocalPlaceholder] = React.useState(content.placeholder || "");
  const debounceRef = React.useRef(null);
  
  // MA-BE-077: Track latest content in ref to avoid stale closures in debounced callbacks
  const contentRef = React.useRef(content);
  React.useEffect(() => {
    contentRef.current = content;
  }, [content]);
  
  // Sync local state when node changes
  React.useEffect(() => {
    setLocalPlaceholder(content.placeholder || "");
  }, [node?.id, content.placeholder]);
  
  // Cleanup debounce timer
  React.useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);
  
  // MA-BE-077: Use contentRef.current to always get latest content
  const debouncedUpdate = (key, value) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const latestContent = contentRef.current;
      onUpdate({ content: { ...latestContent, [key]: value } });
    }, 500);
  };
  
  // MA-BE-077: Use contentRef.current for immediate updates too
  const updateContent = (key, value) => {
    const latestContent = contentRef.current;
    onUpdate({ content: { ...latestContent, [key]: value } });
  };
  
  // MA-ARCH-020B: Edge-based routing handler for "After submission"
  const handleAfterSubmissionRoute = (targetNodeId) => {
    if (onSetDefaultRoute) {
      onSetDefaultRoute(node?.id, targetNodeId);
    } else {
      // Legacy fallback - MA-BE-077: Use contentRef for latest content
      console.warn('[MA-ARCH-020B] Legacy fallback: InputFieldSettings writing to node field directly');
      const latestContent = contentRef.current;
      onUpdate({ content: { ...latestContent, default_next_step_id: targetNodeId } });
    }
  };
  
  // Available nodes for routing (excluding current node)
  const availableNodes = nodes.filter(n => n.id !== node?.id);
  
  // Input type options
  // MA-UI-108: Added "zip" as first-class input type for ZIP Intelligence enrichment
  const INPUT_TYPES = [
    { value: "text", label: "Text" },
    { value: "email", label: "Email" },
    { value: "phone", label: "Phone" },
    { value: "name", label: "Full Name" },
    { value: "first_name", label: "First Name" },
    { value: "last_name", label: "Last Name" },
    { value: "zip", label: "Zip Code" },
  ];
  
  return (
    <div className="space-y-4">
      {/* Input Label/Placeholder */}
      <div className="space-y-2">
        <Label className="text-xs text-slate-300">Placeholder Text</Label>
        <Input
          value={localPlaceholder}
          onChange={(e) => {
            setLocalPlaceholder(e.target.value);
            debouncedUpdate("placeholder", e.target.value);
          }}
          onBlur={() => {
            if (debounceRef.current) {
              clearTimeout(debounceRef.current);
              debounceRef.current = null;
            }
            // MA-BE-078: Use contentRef.current for latest content
            const latestContent = contentRef.current;
            if (localPlaceholder !== latestContent.placeholder) {
              onUpdate({ content: { ...latestContent, placeholder: localPlaceholder } });
            }
          }}
          placeholder="e.g., Enter your email"
          className="bg-white/5 border-white/10 text-slate-100 text-sm"
          data-testid="input-field-placeholder-input"
        />
        <p className="text-xs text-slate-400">
          The hint text shown inside the input field
        </p>
      </div>
      
      <Separator className="bg-white/10" />
      
      {/* Input Type */}
      <div className="space-y-2">
        <Label className="text-xs text-slate-300">Input Type</Label>
        <Select
          value={content.input_type || "text"}
          onValueChange={(value) => updateContent("input_type", value)}
        >
          <SelectTrigger className="h-9 bg-white/5 text-slate-100 border-white/10 text-sm" data-testid="input-type-selector">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-900/95 backdrop-blur-lg border-white/10">
            {INPUT_TYPES.map(type => (
              <SelectItem key={type.value} value={type.value} className="text-slate-200">
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-slate-400">
          Determines keyboard type and validation on mobile
        </p>
      </div>
      
      <Separator className="bg-white/10" />
      
      {/* Input Field Color */}
      <ColorPicker
        label="Input Field Color"
        value={content.input_color || "#007AFF"}
        onChange={(color) => updateContent("input_color", color)}
        presets={COLOR_PRESETS.prospect}
      />
      <p className="text-xs text-slate-400 -mt-2">
        Color of the submit button on the input field
      </p>
      
      <Separator className="bg-white/10" />
      
      {/* MA-CONSENT-TOGGLE-ARCH-APPROVAL-045: Routing Ownership - Consent OFF shows routing here */}
      {!requiresConsent && (
        <>
          {/* After Submission → Send to Step (MA-ARCH-020B: Edge-based routing) */}
          <div className="space-y-2">
            <Label className="text-xs text-slate-300">After Submission → Send to Step</Label>
            <Select
              value={content.default_next_step_id || ""}
              onValueChange={handleAfterSubmissionRoute}
            >
              <SelectTrigger className="h-9 bg-white/5 text-slate-100 border-white/10 text-sm" data-testid="input-routing-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900/95 backdrop-blur-lg border-white/10">
                {/* MA-FUNNEL-ROUTING-052: Terminal Steps from real nodes */}
                {availableNodes.filter(n => n.content?.is_terminal).length > 0 && (
                  <SelectGroup>
                    <SelectLabel className="text-slate-400 text-xs">Terminal Steps</SelectLabel>
                    {availableNodes.filter(n => n.content?.is_terminal).map(n => {
                      const isQualified = n.content?.outcome === "qualified";
                      return (
                        <SelectItem key={n.id} value={n.id} className="text-slate-200">
                          <div className="flex items-center gap-2">
                            {isQualified ? <CheckCircle2 className="h-3 w-3 text-green-400" /> : <XCircle className="h-3 w-3 text-red-400" />}
                            {n.metadata?.label || n.node_type}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectGroup>
                )}
                
                {availableNodes.filter(n => !n.content?.is_terminal).length > 0 && (
                  <>
                    <SelectSeparator className="bg-white/10" />
                    <SelectGroup>
                      <SelectLabel className="text-slate-400 text-xs">Jump to Step</SelectLabel>
                      {availableNodes.filter(n => !n.content?.is_terminal).map(n => (
                        <SelectItem key={n.id} value={n.id} className="text-slate-200">
                          {n.metadata?.label || n.node_type}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </>
                )}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-400">
              Where to go after the user submits their input
            </p>
          </div>
          
          <Separator className="bg-white/10" />
        </>
      )}
      
      {/* MA-CONSENT-TOGGLE-ARCH-APPROVAL-045: Routing Ownership - Consent ON shows helper text */}
      {requiresConsent && (
        <>
          <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
            <p className="text-xs text-blue-200/90">
              Routing for this step is controlled in Consent Button Settings.
            </p>
          </div>
          
          <Separator className="bg-white/10" />
        </>
      )}
      
      {/* MA-FUNNEL-LEAD-002-V2: Step Behavior Section */}
      <StepBehaviorSettings
        node={node}
        nodes={nodes}
        onUpdate={onUpdate}
      />
    </div>
  );
}

// =============================================================================
// Booking Step Settings Panel (MA-ARCH-082 + MA-BOOK-004)
// MA-BOOK-004: Removed BookingEdgeConditionEditor, using canonical routing model
// =============================================================================

const API_URL = window.location.origin || '';

function BookingStepSettings({ 
  node, 
  nodes,
  edges,
  onUpdate,
  onAddEdge,
  onDeleteEdge,
  onUpdateEdge,
  appearance,
}) {
  const content = node?.content || {};
  const config = content.config || {};
  
  // State for dynamic calendar loading
  const [calendars, setCalendars] = React.useState([]);
  const [calendarsLoading, setCalendarsLoading] = React.useState(true);
  const [calendarsError, setCalendarsError] = React.useState(null);
  
  // MA-UI-100: Local state for text fields (debounced)
  const [localHeading, setLocalHeading] = React.useState(content.heading || "");
  const [localSubheading, setLocalSubheading] = React.useState(content.subheading || "");
  const [localDuration, setLocalDuration] = React.useState(config.duration_minutes || "");
  const [calendarOpen, setCalendarOpen] = React.useState(false);  // Calendar search popover
  
  // MA-BE-078: Track latest content/config in refs to avoid stale closures
  const contentRef = React.useRef(content);
  const configRef = React.useRef(config);
  React.useEffect(() => {
    contentRef.current = content;
    configRef.current = config;
  }, [content, config]);
  
  // Sync local state when node changes (e.g., switching nodes)
  React.useEffect(() => {
    setLocalHeading(content.heading || "");
    setLocalSubheading(content.subheading || "");
    setLocalDuration(config.duration_minutes || "");
  }, [node?.id]);
  
  // Debounce timer ref
  const debounceTimerRef = React.useRef(null);
  
  // Default config values
  // MA-BOOK-002: NO hardcoded fallbacks for calendar_id or location_id
  // MA-BOOK-004: Removed dead fields (booking_mode, post_booking_behavior)
  const bookingConfig = {
    calendar_id: config.calendar_id || "",
    location_id: config.location_id || "",  // MA-BOOK-002: No hardcoded fallback
    theme_mode: config.theme_mode || "inherit_funnel",
    show_timezone: config.show_timezone !== false,
    show_duration: config.show_duration !== false,
    show_calendar_title: config.show_calendar_title || false,
    duration_minutes: config.duration_minutes || null,  // MA-UI-100: Manual duration
    overrides: config.overrides || {},
  };
  
  // MA-BOOK-002: Compute booking node validation status
  const getBookingStatus = () => {
    if (!bookingConfig.calendar_id || !bookingConfig.calendar_id.trim()) {
      return { status: "missing", message: "No calendar assigned" };
    }
    // Check if calendar exists in available list (only after calendars loaded)
    if (!calendarsLoading && calendars.length > 0) {
      const calendarExists = calendars.some(c => c.id === bookingConfig.calendar_id);
      if (!calendarExists) {
        return { status: "invalid", message: "Selected calendar not found" };
      }
    }
    return { status: "configured", message: "Calendar configured" };
  };
  
  const bookingStatus = getBookingStatus();
  
  // Fetch calendars from GHL API on mount
  React.useEffect(() => {
    const fetchCalendars = async () => {
      setCalendarsLoading(true);
      setCalendarsError(null);
      
      try {
        const response = await fetch(`${API_URL}/api/booking/calendars`);
        const data = await response.json();
        
        if (data.success && data.calendars) {
          setCalendars(data.calendars);
          // If no calendar selected and calendars exist, could auto-select first
        } else {
          setCalendarsError(data.error || "Failed to load calendars");
        }
      } catch (err) {
        console.error("[BookingSettings] Error fetching calendars:", err);
        setCalendarsError("Failed to connect to calendar service");
      } finally {
        setCalendarsLoading(false);
      }
    };
    
    fetchCalendars();
  }, [bookingConfig.location_id]);
  
  // Get selected calendar details for location_id auto-population
  const selectedCalendar = calendars.find(c => c.id === bookingConfig.calendar_id);
  
  // MA-BE-078: Use refs for latest state
  const updateConfig = (key, value) => {
    const latestConfig = configRef.current;
    const latestContent = contentRef.current;
    const updatedConfig = { ...latestConfig, [key]: value };
    
    // MA-ARCH-092: Auto-populate location_id when calendar changes
    if (key === "calendar_id") {
      const calendar = calendars.find(c => c.id === value);
      if (calendar?.location_id) {
        updatedConfig.location_id = calendar.location_id;
      }
    }
    
    onUpdate({ content: { ...latestContent, config: updatedConfig } });
  };
  
  // MA-UI-100: Direct update for non-text fields (immediate) - MA-BE-078: Use refs
  const updateContent = (key, value) => {
    const latestContent = contentRef.current;
    onUpdate({ content: { ...latestContent, [key]: value } });
  };
  
  // MA-UI-100: Debounced update for text fields - MA-BE-078: Use refs (removed useCallback)
  const debouncedUpdateContent = (key, value) => {
    // Clear any pending debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Set new debounce timer (600ms delay)
    debounceTimerRef.current = setTimeout(() => {
      const latestContent = contentRef.current;
      onUpdate({ content: { ...latestContent, [key]: value } });
    }, 600);
  };
  
  // MA-UI-100: Debounced update for config fields (like duration) - MA-BE-078: Use refs (removed useCallback)
  const debouncedUpdateConfig = (key, value) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      const latestConfig = configRef.current;
      const latestContent = contentRef.current;
      const updatedConfig = { ...latestConfig, [key]: value };
      onUpdate({ content: { ...latestContent, config: updatedConfig } });
    }, 600);
  };
  
  // Cleanup debounce timer on unmount
  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);
  
  // MA-UI-100: Handle heading change with local state + debounce
  const handleHeadingChange = (e) => {
    const value = e.target.value;
    setLocalHeading(value);
    debouncedUpdateContent("heading", value);
  };
  
  // MA-UI-100: Handle subheading change with local state + debounce
  const handleSubheadingChange = (e) => {
    const value = e.target.value;
    setLocalSubheading(value);
    debouncedUpdateContent("subheading", value);
  };
  
  // MA-UI-100: Handle duration change with local state + debounce
  const handleDurationChange = (e) => {
    const value = e.target.value;
    setLocalDuration(value);
    // Only save if it's a valid number or empty
    const numValue = value === "" ? null : parseInt(value, 10);
    if (value === "" || !isNaN(numValue)) {
      debouncedUpdateConfig("duration_minutes", numValue);
    }
  };
  
  // MA-UI-100: Commit changes on blur (immediate save) - MA-BE-078: Use refs
  const handleHeadingBlur = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    const latestContent = contentRef.current;
    if (localHeading !== latestContent.heading) {
      onUpdate({ content: { ...latestContent, heading: localHeading } });
    }
  };
  
  const handleSubheadingBlur = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    const latestContent = contentRef.current;
    if (localSubheading !== latestContent.subheading) {
      onUpdate({ content: { ...latestContent, subheading: localSubheading } });
    }
  };
  
  const handleDurationBlur = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    const latestConfig = configRef.current;
    const latestContent = contentRef.current;
    const numValue = localDuration === "" ? null : parseInt(localDuration, 10);
    if (numValue !== latestConfig.duration_minutes) {
      const updatedConfig = { ...latestConfig, duration_minutes: numValue };
      onUpdate({ content: { ...latestContent, config: updatedConfig } });
    }
  };
  
  const updateOverride = (key, value) => {
    const updatedOverrides = { ...bookingConfig.overrides, [key]: value };
    updateConfig("overrides", updatedOverrides);
  };
  
  const isCustomTheme = bookingConfig.theme_mode === "custom";
  
  return (
    <div className="space-y-4">
      {/* MA-BOOK-002: Booking Status Banner */}
      {bookingStatus.status !== "configured" && (
        <div 
          className={cn(
            "p-3 rounded-lg border flex items-start gap-3",
            bookingStatus.status === "missing" 
              ? "bg-amber-500/10 border-amber-500/30" 
              : "bg-red-500/10 border-red-500/30"
          )}
          data-testid="booking-status-banner"
        >
          <AlertTriangle className={cn(
            "h-4 w-4 flex-shrink-0 mt-0.5",
            bookingStatus.status === "missing" ? "text-amber-400" : "text-red-400"
          )} />
          <div>
            <p className={cn(
              "text-xs font-medium",
              bookingStatus.status === "missing" ? "text-amber-200" : "text-red-200"
            )}>
              {bookingStatus.status === "missing" ? "Calendar Required" : "Calendar Invalid"}
            </p>
            <p className="text-[10px] text-slate-400 mt-0.5">
              {bookingStatus.message}
            </p>
          </div>
        </div>
      )}
      
      {/* Booking Configuration Section */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wider">Configuration</h4>
          {/* MA-BOOK-002: Status Badge */}
          <span className={cn(
            "text-[10px] px-2 py-0.5 rounded-full font-medium",
            bookingStatus.status === "configured" && "bg-emerald-500/20 text-emerald-300",
            bookingStatus.status === "missing" && "bg-amber-500/20 text-amber-300",
            bookingStatus.status === "invalid" && "bg-red-500/20 text-red-300"
          )} data-testid="booking-status-badge">
            {bookingStatus.status === "configured" && "✓ Configured"}
            {bookingStatus.status === "missing" && "⚠ Missing"}
            {bookingStatus.status === "invalid" && "✕ Invalid"}
          </span>
        </div>
        
        {/* Calendar Selection - Searchable Combobox */}
        <div className="space-y-2">
          <Label className={cn(
            "text-xs",
            bookingStatus.status === "missing" ? "text-amber-300" : "text-slate-300"
          )}>
            Calendar {bookingStatus.status === "missing" && <span className="text-amber-400">*</span>}
          </Label>
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={calendarOpen}
                className={cn(
                  "w-full justify-between h-9 text-sm font-normal hover:bg-white/10",
                  bookingStatus.status === "missing" 
                    ? "bg-amber-500/5 border-amber-500/30 text-slate-100" 
                    : bookingStatus.status === "invalid"
                      ? "bg-red-500/5 border-red-500/30 text-slate-100"
                      : "bg-white/5 text-slate-100 border-white/10"
                )}
                disabled={calendarsLoading}
                data-testid="calendar-selector"
              >
                {calendarsLoading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Loading calendars...
                  </span>
                ) : bookingConfig.calendar_id ? (
                  <span className="flex items-center gap-2">
                    {bookingStatus.status === "invalid" && (
                      <AlertTriangle className="h-3 w-3 text-red-400" />
                    )}
                    {calendars.find(c => c.id === bookingConfig.calendar_id)?.name || (
                      <span className="text-red-300 italic">Calendar not found</span>
                    )}
                  </span>
                ) : (
                  <span className="text-amber-300/70">Select a calendar...</span>
                )}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[300px] p-0 bg-slate-900 border-white/10" align="start">
              <Command className="bg-transparent">
                <CommandInput 
                  placeholder="Search calendars..." 
                  className="h-9 text-slate-100 placeholder:text-slate-500"
                />
                <CommandList>
                  <CommandEmpty className="text-slate-400 text-sm py-6 text-center">
                    {calendarsError || "No calendar found."}
                  </CommandEmpty>
                  <CommandGroup>
                    {/* MA-BOOK-002: Removed "No calendar selected" option - calendar is required */}
                    {calendars.map((calendar) => (
                      <CommandItem
                        key={calendar.id}
                        value={calendar.name}
                        onSelect={() => {
                          updateConfig("calendar_id", calendar.id);
                          setCalendarOpen(false);
                        }}
                        className="text-slate-200"
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            bookingConfig.calendar_id === calendar.id ? "opacity-100" : "opacity-0"
                          )}
                        />
                        {calendar.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <p className="text-[10px] text-slate-500">
            {calendarsError ? (
              <span className="text-red-400">Failed to load calendars</span>
            ) : calendars.length === 0 && !calendarsLoading ? (
              <span className="text-amber-400">No calendars available. Configure in GHL.</span>
            ) : (
              `${calendars.length} calendar${calendars.length !== 1 ? 's' : ''} available`
            )}
          </p>
        </div>
        
        {/* Location ID (Advanced) */}
        <div className="space-y-2">
          <Label className="text-xs text-slate-300">Location ID</Label>
          <Input
            value={bookingConfig.location_id}
            onChange={(e) => updateConfig("location_id", e.target.value)}
            className="h-9 bg-white/5 text-slate-100 border-white/10 text-sm font-mono text-xs"
            placeholder="waa4Ckpna9DBcLA3LBEA"
          />
          <p className="text-[10px] text-slate-500">
            {selectedCalendar?.location_id && selectedCalendar.location_id === bookingConfig.location_id ? (
              <span className="text-green-400">✓ Auto-populated from calendar</span>
            ) : (
              "GHL Location ID for this booking"
            )}
          </p>
        </div>
        
        {/* MA-BOOK-004 + MA-ROUTE-003: Unified "Send to Step" routing using shared dropdown pattern */}
        {/* MA-FUNNEL-THEME-DEBUG-089: Hydrate from canonical edges, not stale content.target */}
        <div className="space-y-2">
          <Label className="text-xs text-slate-300">Send to Step</Label>
          <Select
            value={(() => {
              // Read from canonical edge first, fall back to content.target
              const outEdge = edges?.find(e => e.source_node_id === node.id);
              return outEdge?.target_node_id || content.target || "";
            })()}
            onValueChange={(value) => {
              const newTarget = value || null;
              // Update content.target for projection
              onUpdate({
                ...node,
                content: {
                  ...content,
                  target: newTarget
                }
              });
              // Also update the canonical edge
              const existingEdge = edges?.find(e => e.source_node_id === node.id);
              if (existingEdge && newTarget) {
                onUpdateEdge({ ...existingEdge, target_node_id: newTarget });
              } else if (!existingEdge && newTarget) {
                onAddEdge({ source_node_id: node.id, target_node_id: newTarget });
              } else if (existingEdge && !newTarget) {
                onDeleteEdge(existingEdge.id);
              }
            }}
          >
            <SelectTrigger className="h-9 bg-white/5 text-slate-100 border-white/10 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-900/95 backdrop-blur-lg border-white/10">
              {/* MA-FUNNEL-ROUTING-052: Terminal Steps from real nodes */}
              {nodes.filter(n => n.id !== node.id && n.content?.is_terminal).length > 0 && (
                <SelectGroup>
                  <SelectLabel className="text-slate-400 text-xs">Terminal Steps</SelectLabel>
                  {nodes.filter(n => n.id !== node.id && n.content?.is_terminal).map(n => {
                    const isQualified = n.content?.outcome === "qualified";
                    return (
                      <SelectItem key={n.id} value={n.id} className="text-slate-200">
                        <div className="flex items-center gap-2">
                          {isQualified ? <CheckCircle2 className="h-3 w-3 text-green-400" /> : <XCircle className="h-3 w-3 text-red-400" />}
                          {n.metadata?.label || n.content?.message?.slice(0, 30) || n.id}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectGroup>
              )}
              
              {/* Jump to Step Group */}
              {nodes.filter(n => n.id !== node.id && n.id !== nodes[0]?.id && !n.content?.is_terminal).length > 0 && (
                <>
                  <SelectSeparator className="bg-white/10" />
                  <SelectGroup>
                    <SelectLabel className="text-slate-400 text-xs">Jump to Step</SelectLabel>
                    {nodes
                      .filter(n => n.id !== node.id && n.id !== nodes[0]?.id && !n.content?.is_terminal)
                      .map(n => (
                        <SelectItem key={n.id} value={n.id} className="text-slate-200">
                          {n.metadata?.label || n.content?.message?.slice(0, 30) || n.id}
                        </SelectItem>
                      ))
                    }
                  </SelectGroup>
                </>
              )}
            </SelectContent>
          </Select>
          <p className="text-[10px] text-slate-500">
            Where to navigate after booking is completed
          </p>
        </div>
      </div>
      
      <Separator className="bg-white/10" />
      
      {/* Display Options Section */}
      <div className="space-y-3">
        <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wider">Display Options</h4>
        
        {/* Toggle Options */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-xs text-slate-300">Show Timezone</Label>
            <Switch
              checked={bookingConfig.show_timezone}
              onCheckedChange={(checked) => updateConfig("show_timezone", checked)}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <Label className="text-xs text-slate-300">Show Duration</Label>
            <Switch
              checked={bookingConfig.show_duration}
              onCheckedChange={(checked) => updateConfig("show_duration", checked)}
            />
          </div>
          
          {/* MA-UI-100: Duration field - only shown when Show Duration is enabled */}
          {bookingConfig.show_duration && (
            <div className="space-y-2 pl-4 border-l-2 border-indigo-500/30">
              <Label className="text-xs text-slate-300">Duration (minutes)</Label>
              <Input
                type="number"
                value={localDuration}
                onChange={handleDurationChange}
                onBlur={handleDurationBlur}
                className="h-9 bg-white/5 text-slate-100 border-white/10 text-sm w-24"
                placeholder="15"
                min="1"
                max="480"
              />
              <p className="text-[10px] text-slate-500">
                Duration shown on booking slots (e.g., "15 min")
              </p>
            </div>
          )}
          
          <div className="flex items-center justify-between">
            <Label className="text-xs text-slate-300">Show Calendar Title</Label>
            <Switch
              checked={bookingConfig.show_calendar_title}
              onCheckedChange={(checked) => updateConfig("show_calendar_title", checked)}
            />
          </div>
        </div>
      </div>
      
      <Separator className="bg-white/10" />
      
      {/* Theme Section */}
      <div className="space-y-3">
        <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wider">Theme</h4>
        
        {/* Theme Mode Toggle */}
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-xs text-slate-300">Use Funnel Theme</Label>
            <p className="text-[10px] text-slate-500 mt-0.5">
              Inherit colors from funnel appearance
            </p>
          </div>
          <Switch
            checked={bookingConfig.theme_mode === "inherit_funnel"}
            onCheckedChange={(checked) => 
              updateConfig("theme_mode", checked ? "inherit_funnel" : "custom")
            }
          />
        </div>
        
        {/* Custom Theme Overrides - Only shown when custom theme is enabled */}
        {isCustomTheme && (
          <div className="space-y-3 pt-2 pl-2 border-l-2 border-indigo-500/30">
            <ColorPicker
              label="Accent Color"
              value={bookingConfig.overrides.accent_color || appearance?.prospectColor || "#007AFF"}
              onChange={(color) => updateOverride("accent_color", color)}
              presets={COLOR_PRESETS.prospect}
            />
            
            <ColorPicker
              label="Selected Slot Color"
              value={bookingConfig.overrides.slot_selected_color || appearance?.prospectColor || "#007AFF"}
              onChange={(color) => updateOverride("slot_selected_color", color)}
              presets={COLOR_PRESETS.prospect}
            />
            
            <ColorPicker
              label="Confirm Button Color"
              value={bookingConfig.overrides.confirm_button_color || appearance?.prospectColor || "#007AFF"}
              onChange={(color) => updateOverride("confirm_button_color", color)}
              presets={COLOR_PRESETS.prospect}
            />
            
            <ColorPicker
              label="Background Color"
              value={bookingConfig.overrides.background_color || "#FFFFFF"}
              onChange={(color) => updateOverride("background_color", color)}
              presets={COLOR_PRESETS.container}
            />
            
            <ColorPicker
              label="Text Color"
              value={bookingConfig.overrides.text_color || "#111827"}
              onChange={(color) => updateOverride("text_color", color)}
              presets={["#111827", "#1F2937", "#374151", "#4B5563", "#6B7280", "#FFFFFF", "#F9FAFB", "#E5E7EB"]}
            />
          </div>
        )}
        
        {/* Theme Preview Hint */}
        {!isCustomTheme && (
          <div className="p-3 bg-white/5 rounded-lg">
            <p className="text-[10px] text-slate-400">
              Using funnel theme: Accent color will be{" "}
              <span 
                className="inline-block w-3 h-3 rounded align-middle mx-1"
                style={{ backgroundColor: appearance?.prospectColor || "#007AFF" }}
              />
              <span className="font-mono">{appearance?.prospectColor || "#007AFF"}</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Main Properties Panel Component
// =============================================================================
export default function FunnelPropertiesPanel({
  node,
  nodes = [],
  edges = [],
  selectedElement,
  onNodeUpdate,
  onNodeDelete,
  onAddEdge,
  onDeleteEdge,
  onUpdateEdge,
  // MA-ARCH-020B: Edge-based routing mutations
  onSetDefaultRoute,
  onSetResponseRoute,
  onSetTerminal,
  // MA-BE-069: Atomic response deletion
  onDeleteResponse,
  appearance,
  onAppearanceChange,
  header,              // MA-224: Canonical header
  onHeaderChange,      // MA-224: Canonical header update handler
  lpTokens = null,     // MA-269: lpTokens for canonical precedence
  isMutating = false,
}) {
  // Determine what to show based on selection
  const renderContent = () => {
    // MA-ARCH-082: Check if this is a booking step
    if (node?.node_type === "booking") {
      // Show booking settings ONLY when the card is explicitly selected
      if (selectedElement?.type === "booking_widget") {
        return (
          <BookingStepSettings
            node={node}
            nodes={nodes}
            edges={edges}
            onUpdate={onNodeUpdate}
            onAddEdge={onAddEdge}
            onDeleteEdge={onDeleteEdge}
            onUpdateEdge={onUpdateEdge}
            appearance={appearance}
          />
        );
      }
    }
    
    // MA-FUNNEL-LEAD-002-V2: Consent NO LONGER overrides the inspector
    // Consent behaves like all other nodes - shows Appearance by default
    // Submit step toggle is now in the shared StepBehaviorSettings section
    
    if (!selectedElement) {
      // No element selected - show Appearance settings
      return (
        <AppearanceSettings 
          appearance={appearance}
          onAppearanceChange={onAppearanceChange}
          header={header}
          onHeaderChange={onHeaderChange}
          lpTokens={lpTokens}
        />
      );
    }
    
    const { type, id } = selectedElement;
    
    switch (type) {
      case "system_message":
      case "acknowledgement":
        return (
          <BotMessageSettings
            node={node}
            elementId={id}
            onUpdate={onNodeUpdate}
            onDelete={onNodeDelete}
            appearance={appearance}
            onAppearanceChange={onAppearanceChange}
            nodes={nodes}
            // MA-ARCH-020B: Edge-based routing
            onSetDefaultRoute={onSetDefaultRoute}
            onSetTerminal={onSetTerminal}
          />
        );
        
      case "response_button":
        return (
          <UserSettings
            node={node}
            responseId={id}
            onUpdate={onNodeUpdate}
            nodes={nodes}
            appearance={appearance}
            onAppearanceChange={onAppearanceChange}
            // MA-ARCH-020B: Edge-based routing for response options
            onSetResponseRoute={onSetResponseRoute}
            // MA-BE-069: Atomic response deletion
            onDeleteResponse={onDeleteResponse}
          />
        );
        
      case "booking_widget":
      case "booking_card":
        // MA-ARCH-082 + MA-UI-105: Clicking booking card shows booking settings
        return (
          <BookingStepSettings
            node={node}
            nodes={nodes}
            edges={edges}
            onUpdate={onNodeUpdate}
            onAddEdge={onAddEdge}
            onDeleteEdge={onDeleteEdge}
            onUpdateEdge={onUpdateEdge}
            appearance={appearance}
          />
        );
      
      // MA-UI-105: Booking section-specific panels
      case "booking_header":
        return (
          <BookingHeaderSettings
            node={node}
            onUpdate={onNodeUpdate}
          />
        );
      
      // MA-FUNNEL-ARCH-082: Individual heading/subheading panels
      case "booking_heading":
        return (
          <BookingHeadingSettings
            node={node}
            onUpdate={onNodeUpdate}
          />
        );
      
      case "booking_subheading":
        return (
          <BookingSubheadingSettings
            node={node}
            onUpdate={onNodeUpdate}
          />
        );
        
      case "booking_today":
        return (
          <BookingTodaySettings
            node={node}
            onUpdate={onNodeUpdate}
          />
        );
        
      case "booking_tomorrow":
        return (
          <BookingTomorrowSettings
            node={node}
            onUpdate={onNodeUpdate}
          />
        );
      
      // Consent step element-specific panels
      case "consent_title":
        return (
          <ConsentTitleSettings
            node={node}
            onUpdate={onNodeUpdate}
          />
        );
        
      case "consent_text":
        return (
          <ConsentTextSettings
            node={node}
            onUpdate={onNodeUpdate}
          />
        );
        
      case "consent_button":
        return (
          <ConsentButtonSettings
            node={node}
            nodes={nodes}
            onUpdate={onNodeUpdate}
            onSetDefaultRoute={onSetDefaultRoute}
          />
        );
      
      // MA-FE-INPUT-UPDATE: Input field settings panel
      case "input_field":
        return (
          <InputFieldSettings
            node={node}
            nodes={nodes}
            onUpdate={onNodeUpdate}
            onSetDefaultRoute={onSetDefaultRoute}
          />
        );
        
      case "header":
        // Header click shows appearance
        return (
          <AppearanceSettings 
            appearance={appearance}
            onAppearanceChange={onAppearanceChange}
            header={header}
            onHeaderChange={onHeaderChange}
            lpTokens={lpTokens}
          />
        );
        
      default:
        return (
          <AppearanceSettings 
            appearance={appearance}
            onAppearanceChange={onAppearanceChange}
            header={header}
            onHeaderChange={onHeaderChange}
            lpTokens={lpTokens}
          />
        );
    }
  };
  
  // MA-UI-080 Section 4: Dynamic panel title based on selection
  const getPanelTitle = () => {
    if (!selectedElement || selectedElement.type === "header") {
      return "Appearance";
    }
    
    switch (selectedElement.type) {
      case "system_message":
        return "Bot Settings";
      case "acknowledgement":
        return "Acknowledgement";
      case "response_button":
        return "User Settings";
      case "booking_widget":
      case "booking_card":
        return "Booking Settings";
      // MA-UI-105: Booking section titles
      case "booking_header":
        return "Header Settings";
      case "booking_heading":
        return "Heading Settings";
      case "booking_subheading":
        return "Subheading Settings";
      case "booking_today":
        return "Today Settings";
      case "booking_tomorrow":
        return "Tomorrow Settings";
      // Consent element titles
      case "consent_title":
        return "Title Settings";
      case "consent_text":
        return "Consent Language";
      case "consent_button":
        return "Consent Button Settings";
      // MA-FE-INPUT-UPDATE: Input field title
      case "input_field":
        return "Input Settings";
      default:
        return "Appearance";
    }
  };
  
  // MA-UI-080 Section 4: Get appropriate icon for panel
  const getPanelIcon = () => {
    if (!selectedElement || selectedElement.type === "header") {
      return <Palette className="h-4 w-4 text-indigo-400" />;
    }
    
    switch (selectedElement.type) {
      case "system_message":
      case "acknowledgement":
      case "response_button":  // Use same chat bubble icon for User Settings
        return <MessageSquare className="h-4 w-4 text-indigo-400" />;
      case "booking_widget":
      case "booking_card":
        return <CalendarIcon className="h-4 w-4 text-indigo-400" />;
      // MA-UI-105: Booking section icons
      case "booking_header":
        return <Type className="h-4 w-4 text-indigo-400" />;
      case "booking_heading":
        return <Type className="h-4 w-4 text-indigo-400" />;
      case "booking_subheading":
        return <Type className="h-4 w-4 text-indigo-400" />;
      case "booking_today":
      case "booking_tomorrow":
        return <CalendarIcon className="h-4 w-4 text-indigo-400" />;
      // Consent element icons - using indigo to match other step types
      case "consent_title":
        return <Type className="h-4 w-4 text-indigo-400" />;
      case "consent_text":
        return <FileCheck className="h-4 w-4 text-indigo-400" />;
      case "consent_button":
        return <CheckCircle className="h-4 w-4 text-indigo-400" />;
      // MA-FE-INPUT-UPDATE: Input field icon
      case "input_field":
        return <TextCursorInput className="h-4 w-4 text-indigo-400" />;
      default:
        return <Palette className="h-4 w-4 text-indigo-400" />;
    }
  };
  
  return (
    <div 
      className="flex flex-col h-full overflow-hidden"
      data-testid="properties-panel"
    >
      {/* MA-UI-080 Section 4: Simplified panel header - no "Properties" subtitle */}
      {/* MA-UI-113: Match header height with Steps panel (py-3) */}
      <div className="px-5 py-3 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center gap-2">
          {getPanelIcon()}
          <h2 className="text-sm font-semibold text-slate-100">
            {getPanelTitle()}
          </h2>
        </div>
      </div>
      
      {/* Panel Content */}
      <ScrollArea className="flex-1">
        <div className="p-5">
          {renderContent()}
        </div>
      </ScrollArea>
    </div>
  );
}
